import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Pencil, Trash, Save, AlertCircle, Users, Headset, MonitorSmartphone, ScrollText, ShieldCheck, Stamp, Upload, CheckCircle2, ClipboardList } from 'lucide-react';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { useWarehouse } from '../../Components/useWarehouse/useWarehouse';
import { Modal } from '../../Components/Modal/Modal';
import SectionIndexCards from '../../Components/Common/SectionIndexCards';
import { printHvActivo, generateHvActivoPdfBlob } from '../../Components/print/printHvActivo';
import { clearQualityPrintConfigCache, getDefaultQualityPrintRows } from '../../Components/print/qualityPrintConfig';
import { api, setCurrentPageContext } from '../../services/api';
import AdministracionUsuariosYRoles from './AdministracionUsuariosYRoles';
import AdminOperationsLogView from './AdminOperationsLogView';

const HV_DIAG_PROTOCOL = [
  {
    category: 'Computadores',
    items: [
      { id: 'pc_boot', label: 'Encendido y Arranque: ¿El equipo inicia el sistema sin errores?' },
      { id: 'pc_case_keyboard', label: 'Estado de Carcasa/Teclado: ¿Sin roturas ni teclas faltantes?' },
      { id: 'pc_performance', label: 'Velocidad de Operación: ¿Fluidez en tareas básicas?' },
    ],
  },
  {
    category: 'Monitores',
    items: [
      { id: 'mon_image', label: 'Calidad de Imagen: ¿Sin manchas, rayas o píxeles muertos?' },
      { id: 'mon_physical', label: 'Estabilidad Física: ¿La base y botones de ajuste funcionan?' },
      { id: 'mon_ports', label: 'Cables y Puertos: ¿Conectores de video y poder en buen estado?' },
      { id: 'mon_screen', label: 'Estado de Pantalla: ¿Táctil funcional y sin fisuras?' },
    ],
  },
  {
    category: 'Celulares',
    items: [
      { id: 'ph_battery', label: 'Salud de Batería: ¿Carga correctamente y mantiene autonomía?' },
      { id: 'ph_connectivity', label: 'Conectividad: ¿Detecta señal móvil y Wi‑Fi sin caídas?' },
    ],
  },
  {
    category: 'Impresoras',
    items: [
      { id: 'pr_quality', label: 'Calidad de Impresión: ¿Texto claro y sin manchas/rayas?' },
      { id: 'pr_paper', label: 'Mecánica de Papel: ¿Tracciona las hojas sin atascos?' },
      { id: 'pr_consumables', label: 'Niveles de Consumo: ¿Tóner/tinta con capacidad operativa?' },
    ],
  },
];

const BITACORA_TIPOS = ['Nueva función', 'Mejora', 'Corrección', 'Refactorización', 'Seguridad', 'Documentación'];
const BITACORA_ESTADOS = ['Implementado', 'En progreso', 'Planificado'];
const BITACORA_PRIORIDADES = ['Alta', 'Media', 'Baja'];
const AUTH_TOKEN_KEY = 'senerfi_auth_token';

const AUDIT_EVENT_LABELS = {
  api_request: 'Actividad API',
  login_success: 'Ingreso exitoso',
  business_mutation: 'Mutación de negocio',
  role_change: 'Cambio de rol',
};

const AUDIT_MODULE_LABELS = {
  'administracion-ti': 'Administración TI',
  'recursos-humanos': 'Recursos Humanos',
  'almacen': 'Almacén',
  'contabilidad': 'Contabilidad',
  'transportes': 'Transportes',
  'seguridad': 'Seguridad',
  'control-operativo': 'Control Operativo',
  'sgi': 'SIG',
  autenticacion: 'Autenticación',
  general: 'General',
};

const buildTodayYmd = () => {
  const today = new Date();
  const yyyy = String(today.getFullYear());
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatAuditDateTime = (value) => {
  if (!value) return 'Sin fecha';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toQueryString = (params = {}) => {
  const entries = Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '');
  if (!entries.length) return '';
  const qs = new URLSearchParams();
  entries.forEach(([key, value]) => qs.append(key, String(value)));
  return `?${qs.toString()}`;
};

const normalizeAuditBasePath = (input = '/') => {
  let value = String(input || '/').trim();
  if (!value) value = '/';
  if (!value.startsWith('/')) value = `/${value}`;
  if (!value.endsWith('/')) value += '/';
  return value.replace(/\/{2,}/g, '/');
};

const resolveAuditApiBase = () => {
  const origin = String(
    process.env.REACT_APP_API_ORIGIN
    || (typeof window !== 'undefined' ? window.location.origin : '')
  ).trim().replace(/\/$/, '');
  const envBase = String(process.env.REACT_APP_API_BASE_PATH || '').trim();
  if (envBase) {
    const normalized = normalizeAuditBasePath(envBase);
    const apiBase = /\/api\/$/i.test(normalized) ? normalized : `${normalized}api/`;
    return { origin, basePath: apiBase.replace(/\/{2,}/g, '/') };
  }
  const inferredBase = (typeof window !== 'undefined' && /^\/senerfi(\/|$)/i.test(window.location.pathname || ''))
    ? '/senerfi/api/'
    : '/api/';
  return { origin, basePath: inferredBase };
};

const getStoredAuthToken = () => {
  try {
    const sessionToken = window.sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (sessionToken) return sessionToken;
  } catch (_) {}
  try {
    const localToken = window.localStorage.getItem(AUTH_TOKEN_KEY);
    if (localToken) return localToken;
  } catch (_) {}
  return '';
};

const buildAuditEndpointUrl = (params = {}) => {
  const { origin, basePath } = resolveAuditApiBase();
  return `${origin}${basePath}admin_audit.php${toQueryString(params)}`;
};

async function fetchAuditRows(params = {}) {
  const headers = { Accept: 'application/json' };
  const auth = getStoredAuthToken();
  if (auth) headers.Authorization = `Bearer ${auth}`;
  try {
    headers['X-Senerfi-Page'] = `${window.location.pathname}${window.location.search || ''}`;
  } catch (_) {}
  const res = await fetch(buildAuditEndpointUrl(params), {
    method: 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 403) {
    throw new Error(json?.error || 'Permiso denegado para consultar auditoría');
  }
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || 'No fue posible cargar la auditoría');
  }
  return json;
}

const buildQualityVersionForm = (sortOrder = 60) => ({
  document_key: '',
  document_name: '',
  code: '',
  version: '',
  effective_date: buildTodayYmd(),
  description: '',
  sort_order: sortOrder,
  active: true,
});

// Secciones reales que hoy renderiza ContabilidadPanel.jsx (Índice de
// secciones). Se fuerzan aquí porque getDefaultPrefs().section.contabilidad
// puede haber quedado guardado antes de que existieran estas secciones (solo
// tenía las 4 claves viejas: libro-diario/cuentas/facturacion/reportes), y
// sin esto el admin no vería casilla para restringirlas.
const CONTABILIDAD_SECTION_KEYS = ['solicitudes', 'novedades', 'solicitud-gastos', 'proveedores', 'centros-costo'];

export function SignatureCell({ cedula, signatureUrl, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const digits = String(cedula || '').replace(/\D+/g, '');
  const inputId = `sig-upload-${digits || 'x'}`;

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !digits) return;
    setBusy(true);
    setError('');
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.people.uploadSignature({ dni: digits, signature_base64: dataUrl });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible cargar la firma.');
      onUploaded(digits, res.signature_url || dataUrl);
    } catch (err) {
      setError(err?.message || 'No fue posible cargar la firma.');
    } finally {
      setBusy(false);
    }
  };

  if (!digits) {
    return <span className="text-xs text-gray-400">Sin cédula</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {signatureUrl ? (
          <>
            <img src={signatureUrl} alt="Firma" className="h-7 border rounded bg-white object-contain" style={{ maxWidth: 70 }} />
            <CheckCircle2 size={14} className="text-green-600 shrink-0" />
          </>
        ) : (
          <Stamp size={14} className="text-gray-400 shrink-0" />
        )}
        <label
          htmlFor={inputId}
          className="cursor-pointer flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
        >
          <Upload size={12} /> {busy ? 'Cargando...' : signatureUrl ? 'Cambiar' : 'Cargar firma'}
        </label>
        <input id={inputId} type="file" accept="image/*" className="hidden" disabled={busy} onChange={handleFile} />
      </div>
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

export default function AdministracionTIPanel({ location, showLocation = true }) {
  const displayNames = {
    'popayan': 'Popayan',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;

  const { users, currentUser, addUser, updateUser, deleteUser, setUserActive, getModulePrefs, getModulePrefsRaw, setModulePrefs, getSectionPrefs, getSectionPrefsRaw, setSectionPrefs, getDefaultPrefs, saveDefaultPrefs, hasStoredPrefs } = useUserPrefs();
  const { warehouses, people, assignments, materials, brandItems } = useWarehouse();
  const onlyDigits = (v) => String(v || '').replace(/\D+/g, '');
  // Firma digital cargada por colaborador (people.firma_digital), indexada
  // por cédula sin puntos/guiones — para saber a quién le falta cargar
  // firma en "Usuarios y Roles" sin depender de una consulta por fila.
  const [signatureOverrides, setSignatureOverrides] = useState({});
  const signatureByCedula = useMemo(() => {
    const map = {};
    (Array.isArray(people) ? people : []).forEach((p) => {
      const digits = onlyDigits(p.dni);
      if (digits && p.signature) map[digits] = p.signature;
    });
    return { ...map, ...signatureOverrides };
  }, [people, signatureOverrides]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'Administrador',
    password: '',
    warehouseId: 'popayan',
    responsibleCedula: '',
    areaName: '',
    purchaseCanApproveRequests: false,
    purchaseCanApproveOrders: false,
    purchaseCanManageRequests: false,
    purchaseCanEditOrders: false,
    overtimeFinalApprover: false,
    preopAuthSst: false,
    preopAuthSeguridad: false,
    preopAuthTransportes: false,
    overtimeCanEditApproved: false,
    hrNoveltiesCanExport: false,
    expenseCanExport: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [assignCedula, setAssignCedula] = useState({});
  const [statusMsg, setStatusMsg] = useState(null);
  const [view, setView] = useState('permisos'); // 'permisos' | 'crear'
  const [adminTiView, setAdminTiView] = useState(() => { try { return localStorage.getItem('senerfi_ui_admin_ti_view') || 'usuarios'; } catch (_) { return 'usuarios'; } });
  // Reporta a Administración TI > Auditoría qué submódulo de
  // Administración TI está viendo el usuario (ver X-Senerfi-Page en
  // src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/administracion-ti/${adminTiView || ''}`);
  }, [location, adminTiView]);
  const [adminTiGroup, setAdminTiGroup] = useState(() => { try { return localStorage.getItem('senerfi_ui_admin_ti_group') || 'gestion'; } catch (_) { return 'gestion'; } }); // 'gestion' | null
  const [onlyAllowedModules, setOnlyAllowedModules] = useState(false);
  const [onlyAllowedSections, setOnlyAllowedSections] = useState(false);
  const [savingPrefsUserId, setSavingPrefsUserId] = useState(null);
  const [supportsQuery] = useState('');
  const [supportsStatus, setSupportsStatus] = useState('Todos');
  const [supportsPriority, setSupportsPriority] = useState('Todos');
  const [supportsAssignedTo] = useState('Todos');
  const [supportsRows, setSupportsRows] = useState([]);
  const [supportsLoading, setSupportsLoading] = useState(false);
  const [supportsForm, setSupportsForm] = useState({ subject: '', description: '', category: 'General', priority: 'Media', requester_name: '', requester_dni: '', requester_email: '', assigned_to_admin_id: '' });
  const [supportsCreateOpen, setSupportsCreateOpen] = useState(false);
  const [supportsEditOpen, setSupportsEditOpen] = useState(false);
  const [supportsEditing, setSupportsEditing] = useState(null);
  const [hvQuery, setHvQuery] = useState('');
  const [hvType, setHvType] = useState('Todos');
  const [hvStatus, setHvStatus] = useState('Todos');
  const [hvRows, setHvRows] = useState([]);
  const [hvLoading, setHvLoading] = useState(false);
  const [hvCreateOpen, setHvCreateOpen] = useState(false);
  const [hvEditOpen, setHvEditOpen] = useState(false);
  const [hvEditing, setHvEditing] = useState(null);
  const [hvSaving, setHvSaving] = useState(false);
  const [hvResponsibleHistory, setHvResponsibleHistory] = useState({});
  const [hvInspectionModalOpen, setHvInspectionModalOpen] = useState(false);
  const [hvInspectionSerial, setHvInspectionSerial] = useState('');
  const [hvInspectionFiles, setHvInspectionFiles] = useState([]);
  const [hvInspectionLoading, setHvInspectionLoading] = useState(false);
  const [hvInspectionSaving, setHvInspectionSaving] = useState(false);
  const [bitacoraQuery, setBitacoraQuery] = useState('');
  const [bitacoraTipo, setBitacoraTipo] = useState('Todos');
  const [bitacoraRows, setBitacoraRows] = useState([]);
  const [bitacoraTotal, setBitacoraTotal] = useState(0);
  const [bitacoraLastVersion, setBitacoraLastVersion] = useState(null);
  const [bitacoraLoading, setBitacoraLoading] = useState(false);
  const [bitacoraSaving, setBitacoraSaving] = useState(false);
  const [bitacoraEditId, setBitacoraEditId] = useState(null);
  const [bitacoraForm, setBitacoraForm] = useState(() => {
    const today = new Date();
    const yyyy = String(today.getFullYear());
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return {
      version: 'v1.0.0',
      fecha: `${yyyy}-${mm}-${dd}`,
      titulo: '',
      descripcion: '',
      modulo_componente: '',
      responsable: '',
      tipo_cambio: 'Nueva función',
      estado: 'Implementado',
      prioridad: 'Media',
    };
  });
  const [qualityVersionQuery, setQualityVersionQuery] = useState('');
  const [qualityVersionRows, setQualityVersionRows] = useState(() => getDefaultQualityPrintRows());
  const [qualityVersionTotal, setQualityVersionTotal] = useState(() => getDefaultQualityPrintRows().length);
  const [qualityVersionLoading, setQualityVersionLoading] = useState(false);
  const [qualityVersionSaving, setQualityVersionSaving] = useState(false);
  const [qualityVersionEditId, setQualityVersionEditId] = useState(null);
  const [qualityVersionForm, setQualityVersionForm] = useState(() => buildQualityVersionForm(getDefaultQualityPrintRows().length * 10 + 10));
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState('');
  const [auditUserId, setAuditUserId] = useState('Todos');
  const [auditModule, setAuditModule] = useState('Todos');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditDateTimeFrom, setAuditDateTimeFrom] = useState('');
  const [auditDateTimeTo, setAuditDateTimeTo] = useState('');
  const [auditEventType, setAuditEventType] = useState('Todos');
  const [auditMeta, setAuditMeta] = useState({ total: 0, modules: [], eventTypes: [] });
  const [auditView, setAuditView] = useState('tabla'); // tabla | timeline
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const AUDIT_PAGE_SIZE = 500;

  const modulePrefs = useMemo(() => getModulePrefs(currentUser?.id), [getModulePrefs, currentUser]);
  const sectionPrefs = useMemo(() => getSectionPrefs(currentUser?.id), [getSectionPrefs, currentUser]);
  const canViewAuditSection = useMemo(() => {
    if (!modulePrefs?.['administracion-ti']) return false;
    if (String(currentUser?.role || '').trim() === 'Administrador') return true;
    const tiSections = sectionPrefs?.['administracion-ti'] || {};
    return !!tiSections.auditoria;
  }, [currentUser?.role, modulePrefs, sectionPrefs]);
  const canManageUsersAndRoles = useMemo(
    () => String(currentUser?.email || '').trim().toLowerCase() === 'admin@senerfi.com',
    [currentUser?.email],
  );
  const getEditableModulePrefs = useCallback((userId) => (
    canManageUsersAndRoles ? (getModulePrefsRaw?.(userId) || {}) : (getModulePrefs(userId) || {})
  ), [canManageUsersAndRoles, getModulePrefs, getModulePrefsRaw]);
  const getEditableSectionPrefs = useCallback((userId) => (
    canManageUsersAndRoles ? (getSectionPrefsRaw?.(userId) || {}) : (getSectionPrefs(userId) || {})
  ), [canManageUsersAndRoles, getSectionPrefs, getSectionPrefsRaw]);
  const auditStats = useMemo(() => {
    const total = auditMeta.total || auditRows.length;
    const loginCount = auditRows.filter((row) => row?.event_type === 'login_success').length;
    const uniqueUsers = new Set(auditRows.map((row) => row?.admin_user_id).filter(Boolean)).size;
    return { total, loginCount, uniqueUsers };
  }, [auditMeta.total, auditRows]);
  const auditModuleOptions = useMemo(() => {
    const fromApi = Array.isArray(auditMeta.modules) ? auditMeta.modules.map((item) => String(item?.module_key || '').trim()).filter(Boolean) : [];
    const fromRows = auditRows.map((row) => String(row?.module_key || '').trim()).filter(Boolean);
    return Array.from(new Set([...fromApi, ...fromRows]));
  }, [auditMeta.modules, auditRows]);
  const auditEventTypeOptions = useMemo(() => {
    const fromApi = Array.isArray(auditMeta.eventTypes)
      ? auditMeta.eventTypes.map((item) => String(item?.event_type || '').trim()).filter(Boolean)
      : [];
    const fromRows = auditRows.map((row) => String(row?.event_type || '').trim()).filter(Boolean);
    return Array.from(new Set([...fromApi, ...fromRows, 'business_mutation', 'api_request', 'login_success'])).sort();
  }, [auditMeta.eventTypes, auditRows]);
  const auditDateTimeParam = useCallback((localValue) => {
    const v = String(localValue || '').trim();
    if (!v) return undefined;
    const normalized = v.length === 16 ? `${v}:00` : v;
    return normalized.replace('T', ' ');
  }, []);

  // Reconstruye, por usuario, su recorrido cronológico: cada evento de
  // "Ingreso exitoso" abre una sesión nueva y las acciones siguientes
  // quedan anidadas debajo, hasta el próximo login (o el final de los
  // datos consultados). No requiere una llamada nueva al backend: usa los
  // mismos auditRows ya traídos por los filtros de arriba.
  const auditTimelineGroups = useMemo(() => {
    const toTime = (v) => {
      const t = new Date(String(v || '').replace(' ', 'T')).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const byUser = new Map();
    auditRows.forEach((row) => {
      const key = row.admin_user_id != null ? `id-${row.admin_user_id}` : `anon-${row.admin_user_email || row.admin_user_name || 'desconocido'}`;
      if (!byUser.has(key)) {
        byUser.set(key, {
          key,
          userId: row.admin_user_id ?? null,
          name: row.admin_user_name || 'Usuario',
          email: row.admin_user_email || '',
          rows: [],
        });
      }
      byUser.get(key).rows.push(row);
    });
    const groups = Array.from(byUser.values()).map((g) => {
      const sorted = g.rows.slice().sort((a, b) => toTime(a.created_at) - toTime(b.created_at));
      const sessions = [];
      let current = null;
      sorted.forEach((row) => {
        const isLogin = row.event_type === 'login_success';
        if (isLogin || !current) {
          current = { loginRow: isLogin ? row : null, events: [] };
          sessions.push(current);
          if (isLogin) return;
        }
        current.events.push(row);
      });
      const lastActivity = sorted.length ? sorted[sorted.length - 1].created_at : null;
      return { ...g, sessions, lastActivity, totalEvents: sorted.length };
    });
    groups.sort((a, b) => toTime(b.lastActivity) - toTime(a.lastActivity));
    return groups;
  }, [auditRows]);

  // Permisos durante creación de usuario (independientes del usuario seleccionado)
  const moduleDefaultsForRole = (r) => {
    switch (r) {
      case 'Administrador':
        return { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true };
      case 'Administrativo':
        return { 'sgi': true };
      case 'Operativo':
        return { 'control-operativo': true, 'sgi': true };
      case 'Operativo-Inspector':
        return { 'control-operativo': true, 'sgi': true };
      case 'Almacen':
        return { 'almacen': true, 'sgi': true };
      case 'Recursos Humanos':
        return { 'recursos-humanos': true, 'contabilidad': true };
      case 'Contabilidad':
        return { 'recursos-humanos': true, 'contabilidad': true };
      case 'Transportes':
        return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': true, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
      default:
        return { 'recursos-humanos': true, 'almacen': false, 'sgi': true, 'contabilidad': true, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
    }
  };

  const sectionDefaultsForRole = (r) => {
    const base = {
      'almacen': {
        'marca-item': true,
        'materiales': true,
        'bodegas': true,
        'pedidos': true,
        'entrada': true,
        'asignacion': true,
        'prestamo': true,
        'devoluciones': true,
        'salidas': true,
        'traslados': true,
        'consumibles': true,
        'stock': true,
        'consulta-responsable': true,
        'consulta-serial': true,
        'historial': true,
        'alertas': true,
        'indicadores': true,
      },
      'recursos-humanos': { 'personas': true },
      'contabilidad': {
        'libro-diario': false,
        'cuentas': false,
        'facturacion': false,
        'reportes': false,
        'solicitudes': false,
        'proveedores': false,
        'centros-costo': false,
        'novedades': false,
        'solicitud-gastos': false,
      },
      'transportes': {
        'flota': false,
        'rutas': false,
        'mantenimiento': false,
        'despachos': false,
      },
      'seguridad': {
        'inspecciones': false,
        'incidentes': false,
        'reportes': false,
      },
      'control-operativo': {
        'planificacion': true,
        'seguimiento': true,
        'incidencias': true,
        'reportes': true,
      },
      'sgi': {
        'formularios': true,
        'indicadores': true,
        'gestion': true,
      },
      'administracion-ti': {},
    };
    switch (r) {
      case 'Administrador':
        return { 
          ...base, 
          'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true }, 
          'transportes': { 'flota': true, 'rutas': true, 'mantenimiento': true, 'despachos': true },
          'seguridad': { 'inspecciones': true, 'incidentes': true, 'reportes': true }
        };
      case 'Administrativo':
        return {
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'recursos-humanos': { 'personas': false },
          'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
          'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true },
        };
      case 'Operativo':
        return {
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'recursos-humanos': { 'personas': false },
          'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': true, 'seguimiento': true, 'incidencias': true, 'reportes': true },
          'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true },
        };
      case 'Operativo-Inspector':
        return {
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'recursos-humanos': { 'personas': false },
          'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': true, 'seguimiento': true, 'incidencias': true, 'reportes': true },
          'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true },
        };
      case 'Almacen':
        return {
          'almacen': { 'marca-item': true, 'materiales': true, 'entrada': true, 'asignacion': true, 'devoluciones': true, 'salidas': true, 'stock': true, 'consulta-responsable': true, 'historial': true },
          'recursos-humanos': { 'personas': false },
          'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
          'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true },
        };
      case 'Recursos Humanos':
        return {
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'recursos-humanos': { 'personas': true },
          'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
          'sgi': { 'formularios': false, 'indicadores': false, 'gestion': false },
        };
      case 'Contabilidad':
        return {
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'recursos-humanos': { 'personas': true },
          'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true },
          'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
          'sgi': { 'formularios': false, 'indicadores': false, 'gestion': false },
        };
      case 'Transportes':
        return {
          ...base,
          'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
          'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
          'transportes': { 'flota': true, 'rutas': true, 'mantenimiento': true, 'despachos': true },
          'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
          'recursos-humanos': { 'personas': false },
        };
      default:
        return { ...base };
    }
  };

  const [newModulePrefs, setNewModulePrefs] = useState(moduleDefaultsForRole(form.role));
  const [newSectionPrefs, setNewSectionPrefs] = useState(sectionDefaultsForRole(form.role));

  // Evitar sobreescribir los permisos seleccionados al resetear el formulario
  const skipDefaultsOnceRef = React.useRef(false);
  const appliedPersistedDefaultsRef = React.useRef(false);

  React.useEffect(() => {
    // Cuando cambia el rol en creación, recalcular permisos por defecto si no se indicó omitir
    if (!editingId) {
      if (skipDefaultsOnceRef.current || appliedPersistedDefaultsRef.current) {
        skipDefaultsOnceRef.current = false;
        return;
      }
      setNewModulePrefs(moduleDefaultsForRole(form.role));
      setNewSectionPrefs(sectionDefaultsForRole(form.role));
    }
  }, [form.role, editingId]);

  // Al entrar en vista crear (y no editar), aplicar permisos por defecto persistidos desde BD
  React.useEffect(() => {
    if (view === 'crear' && !editingId) {
      const { module, section } = getDefaultPrefs();
      setNewModulePrefs(module);
      setNewSectionPrefs(section);
      appliedPersistedDefaultsRef.current = true;
    } else {
      appliedPersistedDefaultsRef.current = false;
    }
  }, [view, editingId, getDefaultPrefs]);

  const resetForm = () => {
    // Omitir un ciclo de defaults para preservar selección anterior
    skipDefaultsOnceRef.current = true;
    setForm({
      name: '',
      email: '',
      role: 'Administrador',
      password: '',
      warehouseId: 'popayan',
      responsibleCedula: '',
      areaName: '',
      purchaseCanApproveRequests: false,
      purchaseCanApproveOrders: false,
      purchaseCanManageRequests: false,
      purchaseCanEditOrders: false,
      overtimeFinalApprover: false,
      preopAuthSst: false,
      preopAuthSeguridad: false,
      preopAuthTransportes: false,
      overtimeCanEditApproved: false,
      hrNoveltiesCanExport: false,
      expenseCanExport: false,
    });
    setEditingId(null);
    setAssignCedula({});
    // NO cambiar de vista al resetear
  };

  const handleSaveUser = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    if (!String(form.responsibleCedula || '').trim()) {
      setStatusMsg({ type: 'error', text: 'La cédula es obligatoria: se usa para iniciar sesión.' });
      setTimeout(() => setStatusMsg(null), 4000);
      return;
    }
    try {
      if (editingId) {
        await updateUser(editingId, form);
        setStatusMsg({ type: 'success', text: 'Cambios guardados' });
        setEditingId(null);
        setTimeout(() => setStatusMsg(null), 2000);
        return;
      }
      await addUser({ ...form, modulePrefs: newModulePrefs, sectionPrefs: newSectionPrefs });
      setStatusMsg({ type: 'success', text: 'Usuario creado. Formulario listo para otro.' });
      resetForm();
      setTimeout(() => setStatusMsg(null), 2000);
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'No fue posible guardar el usuario' });
      setTimeout(() => setStatusMsg(null), 4000);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name,
      email: u.email,
      role: u.role,
      password: u.password || '',
      warehouseId: u.warehouseId || 'popayan',
      responsibleCedula: u.responsibleCedula || u.responsible_cedula || '',
      areaName: u.areaName || u.area_name || '',
      purchaseCanApproveRequests: !!(u.purchaseCanApproveRequests ?? u.purchase_can_approve_requests ?? false),
      purchaseCanApproveOrders: !!(u.purchaseCanApproveOrders ?? u.purchase_can_approve_orders ?? false),
      purchaseCanManageRequests: !!(u.purchaseCanManageRequests ?? u.purchase_can_manage_requests ?? false),
      purchaseCanEditOrders: !!(u.purchaseCanEditOrders ?? u.purchase_can_edit_orders ?? false),
      overtimeFinalApprover: !!(u.overtimeFinalApprover ?? u.overtime_final_approver ?? false),
      preopAuthSst: !!(u.preopAuthSst ?? u.preop_auth_sst ?? false),
      preopAuthSeguridad: !!(u.preopAuthSeguridad ?? u.preop_auth_seguridad ?? false),
      preopAuthTransportes: !!(u.preopAuthTransportes ?? u.preop_auth_transportes ?? false),
      overtimeCanEditApproved: !!(u.overtimeCanEditApproved ?? u.overtime_can_edit_approved ?? false),
      hrNoveltiesCanExport: !!(u.hrNoveltiesCanExport ?? u.hr_novelties_can_export ?? false),
      expenseCanExport: !!(u.expenseCanExport ?? u.expense_can_export ?? false),
    });
    const safeMp = getEditableModulePrefs(u.id) || {};
    const safeSp = getEditableSectionPrefs(u.id) || {};
    setNewModulePrefs(safeMp);
    setNewSectionPrefs(safeSp);
    // No cambiar de vista; edición inline
  };

  const savePrefs = async (userId) => {
    setSavingPrefsUserId(userId);
    const mp = getEditableModulePrefs(userId) || {};
    const sp = getEditableSectionPrefs(userId) || {};
    try {
      await api.admin.prefs.upsert({ user_id: userId, module_prefs: JSON.stringify(mp), section_prefs: JSON.stringify(sp) });
      setStatusMsg({ type: 'success', text: 'Guardado' });
    } catch (e) {
      try { setModulePrefs(userId, mp); } catch {}
      setStatusMsg({ type: 'success', text: 'Guardado' });
    } finally {
      setSavingPrefsUserId(null);
      setTimeout(() => setStatusMsg(null), 2000);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const toggleModule = (key) => {
    setModulePrefs(currentUser?.id, { ...modulePrefs, [key]: !modulePrefs[key] });
  };
  // eslint-disable-next-line no-unused-vars
  const toggleSection = (moduleKey, sectionKey) => {
    const mod = sectionPrefs[moduleKey] || {};
    const next = { ...mod, [sectionKey]: !mod[sectionKey] };
    setSectionPrefs(currentUser?.id, moduleKey, next);
  };

  // Edición inline por fila
  const toggleRowModule = (userId, key) => {
    const mp = getEditableModulePrefs(userId) || {};
    setModulePrefs(userId, { ...mp, [key]: !mp[key] });
  };

  const toggleRowSection = (userId, moduleKey, sectionKey) => {
    const sp = getEditableSectionPrefs(userId) || {};
    const mod = sp[moduleKey] || {};
    const next = { ...mod, [sectionKey]: !mod[sectionKey] };
    setSectionPrefs(userId, moduleKey, next);
  };

  // Toggles durante creación
  const toggleNewModule = (key) => {
    setNewModulePrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  // eslint-disable-next-line no-unused-vars
  const toggleNewSection = (moduleKey, sectionKey) => {
    const mod = newSectionPrefs[moduleKey] || {};
    const next = { ...mod, [sectionKey]: !mod[sectionKey] };
    setNewSectionPrefs((prev) => ({ ...prev, [moduleKey]: next }));
  };

  const getWarehouseName = (id) => {
    const w = (warehouses || []).find((x) => x.id === id);
    return w?.name || displayNames[id] || id;
  };

  const handleSaveDefaults = async () => {
    try {
      await saveDefaultPrefs(newModulePrefs, newSectionPrefs);
      setStatusMsg({ type: 'success', text: 'Predeterminado guardado' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: 'No se pudo guardar predeterminado' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2000);
    }
  };

  const loadSupports = useCallback(async () => {
    setSupportsLoading(true);
    try {
      const isAdmin = String(currentUser?.role || '').trim() === 'Administrador';
      const filters = {
        q: supportsQuery.trim() || undefined,
        order: 'desc',
        limit: 500,
        viewer_admin_id: currentUser?.id ?? undefined,
        include_all: isAdmin ? 1 : 0,
      };
      if (supportsPriority !== 'Todos') filters.priority = supportsPriority;
      if (supportsAssignedTo !== 'Todos') filters.assigned_to_admin_id = supportsAssignedTo;
      const res = await api.admin.supports.list(filters);
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : (res?.data?.data ? res.data.data : []));
      setSupportsRows(rows);
    } catch (_) {
      setSupportsRows([]);
    } finally {
      setSupportsLoading(false);
    }
  }, [currentUser?.id, currentUser?.role, supportsAssignedTo, supportsPriority, supportsQuery]);

  const loadHvAssets = useCallback(async () => {
    setHvLoading(true);
    try {
      const filters = {
        q: hvQuery.trim() || undefined,
        order: 'desc',
        limit: 500,
      };
      if (hvType !== 'Todos') filters.asset_type = hvType;
      if (hvStatus !== 'Todos') filters.status = hvStatus;
      const res = await api.admin.hvAssets.list(filters);
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : (res?.data?.data ? res.data.data : []));
      setHvRows(rows);
    } catch (_) {
      setHvRows([]);
    } finally {
      setHvLoading(false);
    }
  }, [hvQuery, hvStatus, hvType]);

  const loadBitacora = useCallback(async () => {
    setBitacoraLoading(true);
    try {
      const filters = {
        q: bitacoraQuery.trim() || undefined,
        order: 'desc',
        limit: 500,
      };
      if (bitacoraTipo !== 'Todos') filters.tipo_cambio = bitacoraTipo;
      const res = await api.admin.bitacoraCambios.list(filters);
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : (res?.data?.data ? res.data.data : []));
      setBitacoraRows(rows);
      setBitacoraTotal(Number(res?.total ?? rows.length ?? 0));
      setBitacoraLastVersion(res?.last_version ?? null);
    } catch (_) {
      setBitacoraRows([]);
      setBitacoraTotal(0);
      setBitacoraLastVersion(null);
    } finally {
      setBitacoraLoading(false);
    }
  }, [bitacoraQuery, bitacoraTipo]);

  const resetBitacoraForm = () => {
    const today = new Date();
    const yyyy = String(today.getFullYear());
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setBitacoraEditId(null);
    setBitacoraForm((prev) => ({
      version: String(prev?.version || 'v1.0.0'),
      fecha: `${yyyy}-${mm}-${dd}`,
      titulo: '',
      descripcion: '',
      modulo_componente: '',
      responsable: '',
      tipo_cambio: 'Nueva función',
      estado: 'Implementado',
      prioridad: 'Media',
    }));
  };

  const saveBitacora = async () => {
    const titulo = String(bitacoraForm.titulo || '').trim();
    if (!titulo) {
      setStatusMsg({ type: 'error', text: 'Ingrese el título del cambio' });
      setTimeout(() => setStatusMsg(null), 2000);
      return;
    }
    setBitacoraSaving(true);
    try {
      const payload = {
        id: bitacoraEditId || undefined,
        version: String(bitacoraForm.version || '').trim() || null,
        fecha: String(bitacoraForm.fecha || '').trim() || null,
        titulo,
        descripcion: String(bitacoraForm.descripcion || '').trim() || null,
        modulo_componente: String(bitacoraForm.modulo_componente || '').trim() || null,
        responsable: String(bitacoraForm.responsable || '').trim() || null,
        tipo_cambio: String(bitacoraForm.tipo_cambio || '').trim() || null,
        estado: String(bitacoraForm.estado || '').trim() || null,
        prioridad: String(bitacoraForm.prioridad || '').trim() || null,
        created_by_admin_id: currentUser?.id ?? null,
        created_by_admin_name: String(currentUser?.name || currentUser?.email || '').trim() || null,
      };
      const r = await api.admin.bitacoraCambios.upsert(payload);
      if (!r?.ok) throw new Error(r?.error || 'No fue posible guardar el cambio');
      setStatusMsg({ type: 'success', text: bitacoraEditId ? 'Cambio actualizado' : 'Cambio agregado' });
      resetBitacoraForm();
      loadBitacora();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error guardando cambio' });
    } finally {
      setBitacoraSaving(false);
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const deleteBitacora = async (row) => {
    const id = row?.id;
    if (!id) return;
    const ok = window.confirm(`¿Eliminar este cambio?\n\n${String(row?.titulo || '')}`);
    if (!ok) return;
    try {
      const r = await api.admin.bitacoraCambios.delete({ id });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible eliminar el cambio');
      setStatusMsg({ type: 'success', text: 'Cambio eliminado' });
      if (String(bitacoraEditId) === String(id)) resetBitacoraForm();
      loadBitacora();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error eliminando cambio' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const loadQualityVersions = useCallback(async () => {
    setQualityVersionLoading(true);
    try {
      const filters = {
        q: qualityVersionQuery.trim() || undefined,
        active: 'Todos',
        limit: 500,
      };
      const res = await api.admin.qualityVersions.list(filters);
      const rows = Array.isArray(res?.data) ? res.data : getDefaultQualityPrintRows();
      setQualityVersionRows(rows);
      setQualityVersionTotal(Number(res?.total ?? rows.length ?? 0));
    } catch (_) {
      const fallbackRows = getDefaultQualityPrintRows();
      setQualityVersionRows(fallbackRows);
      setQualityVersionTotal(fallbackRows.length);
    } finally {
      setQualityVersionLoading(false);
    }
  }, [qualityVersionQuery]);

  const loadAuditRows = useCallback(async () => {
    setAuditLoading(true);
    try {
      const filters = {
        q: auditQuery.trim() || undefined,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
        user_id: auditUserId !== 'Todos' ? auditUserId : undefined,
        module_key: auditModule !== 'Todos' ? auditModule : undefined,
        event_type: auditEventType !== 'Todos' ? auditEventType : undefined,
        datetime_from: auditDateTimeParam(auditDateTimeFrom),
        datetime_to: auditDateTimeParam(auditDateTimeTo),
        date_from: !auditDateTimeFrom && auditDateFrom ? auditDateFrom : undefined,
        date_to: !auditDateTimeTo && auditDateTo ? auditDateTo : undefined,
      };
      const res = await fetchAuditRows(filters);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setAuditRows(rows);
      setAuditOffset(rows.length);
      setAuditHasMore(rows.length === AUDIT_PAGE_SIZE && rows.length < Number(res?.total || 0));
      setAuditMeta({
        total: Number(res?.total || 0),
        modules: Array.isArray(res?.modules) ? res.modules : [],
        eventTypes: Array.isArray(res?.event_types) ? res.event_types : [],
      });
    } catch (e) {
      setAuditRows([]);
      setAuditMeta({ total: 0, modules: [] });
      setAuditHasMore(false);
      setStatusMsg({ type: 'error', text: e?.message || 'No fue posible cargar la auditoría' });
      setTimeout(() => setStatusMsg(null), 2500);
    } finally {
      setAuditLoading(false);
    }
  }, [auditDateFrom, auditDateTimeFrom, auditDateTimeTo, auditDateTo, auditDateTimeParam, auditEventType, auditModule, auditQuery, auditUserId]);

  // Carga la siguiente página SIN reemplazar lo ya visible — así se puede
  // seguir consultando el historial completo (más de 2 millones de
  // eventos) en vez de quedar limitado a los últimos 500.
  const loadMoreAuditRows = useCallback(async () => {
    setAuditLoadingMore(true);
    try {
      const filters = {
        q: auditQuery.trim() || undefined,
        limit: AUDIT_PAGE_SIZE,
        offset: auditOffset,
        user_id: auditUserId !== 'Todos' ? auditUserId : undefined,
        module_key: auditModule !== 'Todos' ? auditModule : undefined,
        event_type: auditEventType !== 'Todos' ? auditEventType : undefined,
        datetime_from: auditDateTimeParam(auditDateTimeFrom),
        datetime_to: auditDateTimeParam(auditDateTimeTo),
        date_from: !auditDateTimeFrom && auditDateFrom ? auditDateFrom : undefined,
        date_to: !auditDateTimeTo && auditDateTo ? auditDateTo : undefined,
      };
      const res = await fetchAuditRows(filters);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setAuditRows((prev) => [...prev, ...rows]);
      setAuditOffset((prev) => prev + rows.length);
      setAuditHasMore(rows.length === AUDIT_PAGE_SIZE && (auditOffset + rows.length) < Number(res?.total || 0));
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'No fue posible cargar más eventos' });
      setTimeout(() => setStatusMsg(null), 2500);
    } finally {
      setAuditLoadingMore(false);
    }
  }, [auditDateFrom, auditDateTimeFrom, auditDateTimeTo, auditDateTo, auditDateTimeParam, auditEventType, auditModule, auditOffset, auditQuery, auditUserId]);

  const resetQualityVersionForm = () => {
    setQualityVersionEditId(null);
    const nextOrder = (qualityVersionRows.length || getDefaultQualityPrintRows().length) * 10 + 10;
    setQualityVersionForm(buildQualityVersionForm(nextOrder));
  };

  const saveQualityVersion = async () => {
    const documentKey = String(qualityVersionForm.document_key || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    const documentName = String(qualityVersionForm.document_name || '').trim();
    if (!documentKey || !documentName) {
      setStatusMsg({ type: 'error', text: 'Ingrese la clave y el nombre del imprimible' });
      setTimeout(() => setStatusMsg(null), 2000);
      return;
    }
    setQualityVersionSaving(true);
    try {
      const payload = {
        id: qualityVersionEditId || undefined,
        document_key: documentKey,
        document_name: documentName,
        code: String(qualityVersionForm.code || '').trim() || null,
        version: String(qualityVersionForm.version || '').trim() || null,
        effective_date: String(qualityVersionForm.effective_date || '').trim() || null,
        description: String(qualityVersionForm.description || '').trim() || null,
        sort_order: Number(qualityVersionForm.sort_order || 0) || 0,
        active: qualityVersionForm.active ? 1 : 0,
        created_by_admin_id: currentUser?.id ?? null,
        created_by_admin_name: String(currentUser?.name || currentUser?.email || '').trim() || null,
      };
      const r = await api.admin.qualityVersions.upsert(payload);
      if (!r?.ok) throw new Error(r?.error || 'No fue posible guardar el control de versiones');
      clearQualityPrintConfigCache();
      setStatusMsg({ type: 'success', text: qualityVersionEditId ? 'Imprimible actualizado' : 'Imprimible agregado' });
      resetQualityVersionForm();
      loadQualityVersions();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error guardando el imprimible' });
    } finally {
      setQualityVersionSaving(false);
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const deleteQualityVersion = async (row) => {
    const id = row?.id;
    if (!id) return;
    const ok = window.confirm(`¿Eliminar este imprimible?\n\n${String(row?.document_name || '')}`);
    if (!ok) return;
    try {
      const r = await api.admin.qualityVersions.delete({ id });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible eliminar el imprimible');
      clearQualityPrintConfigCache();
      setStatusMsg({ type: 'success', text: 'Imprimible eliminado' });
      if (String(qualityVersionEditId) === String(id)) resetQualityVersionForm();
      loadQualityVersions();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error eliminando el imprimible' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  useEffect(() => {
    if (adminTiView !== 'soportes') return;
    loadSupports();
  }, [adminTiView, loadSupports]);

  useEffect(() => {
    if (adminTiView !== 'hv-activos') return;
    loadHvAssets();
  }, [adminTiView, loadHvAssets]);

  useEffect(() => {
    if (adminTiView !== 'bitacora-cambios') return;
    loadBitacora();
  }, [adminTiView, loadBitacora]);

  useEffect(() => {
    if (adminTiView !== 'control-versiones-calidad') return;
    loadQualityVersions();
  }, [adminTiView, loadQualityVersions]);

  useEffect(() => {
    if (adminTiView !== 'auditoria') return;
    if (!canViewAuditSection) return;
    loadAuditRows();
  }, [adminTiView, canViewAuditSection, loadAuditRows]);

  useEffect(() => { try { localStorage.setItem('senerfi_ui_admin_ti_view', adminTiView || ''); } catch (_) {} }, [adminTiView]);
  useEffect(() => { try { localStorage.setItem('senerfi_ui_admin_ti_group', adminTiGroup || ''); } catch (_) {} }, [adminTiGroup]);

  const createSupport = async () => {
    const subject = String(supportsForm.subject || '').trim();
    if (!subject) {
      setStatusMsg({ type: 'error', text: 'Ingrese el asunto' });
      setTimeout(() => setStatusMsg(null), 2000);
      return;
    }
    try {
      const r = await api.admin.supports.upsert({
        subject,
        description: String(supportsForm.description || '').trim() || null,
        category: String(supportsForm.category || '').trim() || 'General',
        priority: supportsForm.priority || 'Media',
        requester_name: String(supportsForm.requester_name || '').trim() || null,
        requester_dni: String(supportsForm.requester_dni || '').trim() || null,
        requester_email: String(supportsForm.requester_email || '').trim() || null,
        assigned_to_admin_id: supportsForm.assigned_to_admin_id === '' ? null : Number(supportsForm.assigned_to_admin_id),
        status: 'Abierto',
      });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible crear el soporte');
      setSupportsForm({ subject: '', description: '', category: 'General', priority: 'Media', requester_name: '', requester_dni: '', requester_email: '', assigned_to_admin_id: '' });
      setSupportsCreateOpen(false);
      setStatusMsg({ type: 'success', text: 'Soporte creado' });
      loadSupports();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error creando soporte' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2000);
    }
  };

  const openEditSupport = (row) => {
    setSupportsEditing({
      id: row?.id,
      subject: row?.subject || '',
      description: row?.description || '',
      category: row?.category || 'General',
      status: row?.status || 'Abierto',
      priority: row?.priority || 'Media',
      requester_name: row?.requester_name || '',
      requester_dni: row?.requester_dni || '',
      requester_email: row?.requester_email || '',
      assigned_to_admin_id: row?.assigned_to_admin_id || '',
      resolution: row?.resolution || '',
    });
    setSupportsEditOpen(true);
  };

  const saveEditSupport = async () => {
    const s = supportsEditing;
    if (!s?.id) return;
    try {
      const r = await api.admin.supports.upsert({
        id: s.id,
        subject: String(s.subject || '').trim() || null,
        description: String(s.description || '').trim(),
        category: String(s.category || '').trim() || 'General',
        status: s.status,
        priority: s.priority,
        requester_name: String(s.requester_name || '').trim(),
        requester_dni: String(s.requester_dni || '').trim(),
        requester_email: String(s.requester_email || '').trim(),
        assigned_to_admin_id: s.assigned_to_admin_id === '' ? null : Number(s.assigned_to_admin_id),
        resolution: String(s.resolution || '').trim(),
      });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible guardar');
      setSupportsEditOpen(false);
      setSupportsEditing(null);
      setStatusMsg({ type: 'success', text: 'Soporte actualizado' });
      loadSupports();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error guardando soporte' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2000);
    }
  };

  const deleteSupport = async (id) => {
    if (!id) return;
    const ok = window.confirm('¿Eliminar este soporte?');
    if (!ok) return;
    try {
      const r = await api.admin.supports.delete({ id });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible eliminar');
      setStatusMsg({ type: 'success', text: 'Soporte eliminado' });
      loadSupports();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error eliminando soporte' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2000);
    }
  };

  const supportsFilteredRows = useMemo(() => {
    const rows = Array.isArray(supportsRows) ? supportsRows : [];
    if (supportsStatus === 'Todos') return rows;
    return rows.filter((r) => String(r?.status || '').trim() === supportsStatus);
  }, [supportsRows, supportsStatus]);

  const supportsCounts = useMemo(() => {
    const rows = Array.isArray(supportsRows) ? supportsRows : [];
    let abiertos = 0;
    let enProceso = 0;
    let cerrados = 0;
    rows.forEach((r) => {
      const st = String(r?.status || '').trim();
      if (st === 'Abierto') abiertos += 1;
      else if (st === 'En Proceso') enProceso += 1;
      else if (st === 'Cerrado') cerrados += 1;
    });
    return { total: rows.length, abiertos, enProceso, cerrados };
  }, [supportsRows]);

  const formatTicketCode = (id) => {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return 'TK-000';
    return `TK-${String(n).padStart(3, '0')}`;
  };

  const statusPillClass = (status) => {
    const s = String(status || '').trim();
    if (s === 'Abierto') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s === 'En Proceso') return 'bg-yellow-50 text-yellow-800 border-yellow-200';
    if (s === 'Cerrado') return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const priorityPillClass = (priority) => {
    const p = String(priority || '').trim();
    if (p === 'Alta') return 'bg-red-50 text-red-700 border-red-200';
    if (p === 'Media') return 'bg-yellow-50 text-yellow-800 border-yellow-200';
    if (p === 'Baja') return 'bg-green-50 text-green-700 border-green-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const [dashFrom, setDashFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dashTo, setDashTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [dashPreset, setDashPreset] = useState('month');
  const dashboardData = useMemo(() => {
    const rows = Array.isArray(supportsRows) ? supportsRows : [];
    const parseDt = (v) => {
      const s = String(v || '').trim();
      if (!s) return null;
      const d = new Date(s.replace(' ', 'T'));
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const fromDt = dashFrom ? new Date(dashFrom + 'T00:00:00') : null;
    const toDt = dashTo ? new Date(dashTo + 'T23:59:59') : null;
    const inRange = (d) => {
      if (!d) return false;
      if (fromDt && d.getTime() < fromDt.getTime()) return false;
      if (toDt && d.getTime() > toDt.getTime()) return false;
      return true;
    };
    const createdInRange = rows.filter((r) => inRange(parseDt(r?.created_at)));

    const totalGenerated = createdInRange.length;
    let pending = 0;
    let inProcess = 0;
    let closed = 0;
    let unassigned = 0;
    let unassignedInProcess = 0;
    createdInRange.forEach((r) => {
      const st = String(r?.status || '').trim();
      if (st === 'Cerrado') closed += 1;
      else if (st === 'En Proceso') inProcess += 1;
      else pending += 1;
      if (st !== 'Cerrado' && (r?.assigned_to_admin_id === null || r?.assigned_to_admin_id === undefined || String(r?.assigned_to_admin_id) === '')) {
        unassigned += 1;
        if (st === 'En Proceso') unassignedInProcess += 1;
      }
    });
    const closedRate = totalGenerated ? Math.round((closed / totalGenerated) * 100) : 0;

    const rangeDays = (() => {
      if (!fromDt || !toDt) return 0;
      const diff = Math.round((toDt.getTime() - fromDt.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(1, diff + 1);
    })();
    const prevFrom = fromDt ? new Date(fromDt.getTime() - (rangeDays * 24 * 60 * 60 * 1000)) : null;
    const prevTo = fromDt ? new Date(fromDt.getTime() - (1 * 24 * 60 * 60 * 1000)) : null;
    const inPrev = (d) => {
      if (!d || !prevFrom || !prevTo) return false;
      if (d.getTime() < prevFrom.getTime()) return false;
      if (d.getTime() > prevTo.getTime()) return false;
      return true;
    };
    const prevTotal = rows.filter((r) => inPrev(parseDt(r?.created_at))).length;
    const pctVsPrev = (() => {
      if (!prevTotal) return null;
      return Math.round(((totalGenerated - prevTotal) / prevTotal) * 100);
    })();

    const daysLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    const idxOf = (d) => {
      const js = d.getDay(); // 0 dom ... 6 sab
      return js === 0 ? 6 : (js - 1);
    };
    const series = daysLabels.map((label) => ({ label, generated: 0, closed: 0, pending: 0 }));
    createdInRange.forEach((r) => {
      const cd = parseDt(r?.created_at);
      if (cd) {
        const i = idxOf(cd);
        series[i].generated += 1;
        if (String(r?.status || '').trim() !== 'Cerrado') series[i].pending += 1;
      }
      const zd = parseDt(r?.closed_at);
      if (zd && inRange(zd)) {
        const i2 = idxOf(zd);
        series[i2].closed += 1;
      }
    });
    const maxSeries = series.reduce((m, it) => Math.max(m, it.generated, it.closed, it.pending), 0) || 1;

    const byCategory = (() => {
      const m = new Map();
      createdInRange.forEach((r) => {
        const cat = String(r?.category || 'General').trim() || 'General';
        m.set(cat, (m.get(cat) || 0) + 1);
      });
      const arr = Array.from(m.entries()).map(([cat, count]) => ({ cat, count }));
      arr.sort((a, b) => b.count - a.count);
      const max = arr.reduce((mx, it) => Math.max(mx, it.count), 0) || 1;
      return { arr, max };
    })();

    const byPriority = (() => {
      const base = { Alta: 0, Media: 0, Baja: 0 };
      createdInRange.forEach((r) => {
        const p = String(r?.priority || 'Media').trim();
        if (p === 'Alta' || p === 'Media' || p === 'Baja') base[p] += 1;
      });
      const total = base.Alta + base.Media + base.Baja;
      const pct = (n) => total ? Math.round((n / total) * 100) : 0;
      return { ...base, total, pctAlta: pct(base.Alta), pctMedia: pct(base.Media), pctBaja: pct(base.Baja) };
    })();

    const avgCloseDays = (() => {
      const closedRows = rows.filter((r) => String(r?.status || '').trim() === 'Cerrado' && inRange(parseDt(r?.closed_at || r?.updated_at)));
      let sum = 0;
      let n = 0;
      closedRows.forEach((r) => {
        const start = parseDt(r?.assigned_at) || parseDt(r?.created_at);
        const end = parseDt(r?.closed_at) || parseDt(r?.updated_at);
        if (!start || !end) return;
        const diff = Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
        sum += diff;
        n += 1;
      });
      return { days: n ? Math.round(sum / n) : 0, n };
    })();

    const byAssigneeClosed = (() => {
      const list = Array.isArray(users) ? users : [];
      const byId = new Map(list.map((u) => [String(u?.id), String(u?.name || u?.email || u?.id || '').trim()]));
      const m = new Map();
      rows.forEach((r) => {
        if (String(r?.status || '').trim() !== 'Cerrado') return;
        const end = parseDt(r?.closed_at || r?.updated_at);
        if (!inRange(end)) return;
        const rawAssigneeId = r?.assigned_to_admin_id;
        const assigneeId = (rawAssigneeId === null || rawAssigneeId === undefined) ? '' : String(rawAssigneeId).trim();
        if (!assigneeId) return;
        const fallbackName = String(r?.assigned_to_name || r?.assigned_to || '').trim();
        const name = byId.get(assigneeId) || fallbackName || `ID ${assigneeId}`;
        m.set(name, (m.get(name) || 0) + 1);
      });
      const arr = Array.from(m.entries()).map(([name, count]) => ({ name, count }));
      arr.sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name)));
      const max = arr.reduce((mx, it) => Math.max(mx, it.count), 0) || 1;
      return { arr, max };
    })();

    return { totalGenerated, pending, inProcess, closed, unassigned, unassignedInProcess, closedRate, pctVsPrev, series, maxSeries, byCategory, byPriority, avgCloseDays, byAssigneeClosed };
  }, [supportsRows, dashFrom, dashTo, users]);

  const hvPeopleOptions = useMemo(() => {
    const arr = Array.isArray(people) ? people : [];
    return arr
      .filter((p) => String(p?.status || 'Activo') === 'Activo')
      .map((p) => ({
        dni: String(p?.dni || p?.cedula || p?.documento_identidad || p?.id || '').trim(),
        name: String(p?.name || p?.nombre || p?.nombre_completo || '').trim(),
      }))
      .filter((p) => p.dni !== '' && p.name !== '')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [people]);

  const hvAdminOptions = useMemo(() => {
    const arr = Array.isArray(users) ? users : [];
    return arr
      .map((u) => ({ id: u?.id, name: String(u?.name || u?.email || u?.id || '').trim() }))
      .filter((u) => u.id !== null && u.id !== undefined && u.name !== '')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const normalizeText = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const normalizePersonDoc = (v) => {
    const raw = String(v ?? '').trim();
    const digits = raw.replace(/\D+/g, '');
    return digits || raw.toLowerCase();
  };

  const hvPeopleIndex = useMemo(() => {
    const arr = Array.isArray(people) ? people : [];
    const m = new Map();
    arr.forEach((p) => {
      const candidates = [
        p?.dni,
        p?.cedula,
        p?.documento_identidad,
        p?.documento,
        p?.doc,
        p?.id,
        p?._dbId,
      ];
      candidates.forEach((c) => {
        const k = normalizePersonDoc(c);
        if (!k) return;
        if (!m.has(k)) m.set(k, p);
      });
    });
    return m;
  }, [people]);

  const findPersonByDoc = (doc) => {
    const k = normalizePersonDoc(doc);
    if (!k) return null;
    return hvPeopleIndex.get(k) || null;
  };

  const materialById = useMemo(() => {
    const arr = Array.isArray(materials) ? materials : [];
    const m = new Map();
    arr.forEach((it) => {
      const id = it?.id;
      if (id === null || id === undefined) return;
      m.set(String(id), it);
    });
    return m;
  }, [materials]);

  const techMaterials = useMemo(() => {
    const arr = Array.isArray(materials) ? materials : [];
    const list = arr.filter((m) => normalizeText(m?.category || m?.type || '').includes('tecn'));
    list.sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')) || String(a.name || '').localeCompare(String(b.name || '')));
    return list;
  }, [materials]);

  const materialByCode = useMemo(() => {
    const m = new Map();
    (Array.isArray(materials) ? materials : []).forEach((it) => {
      const code = String(it?.code || '').trim();
      if (!code) return;
      if (!m.has(code)) m.set(code, it);
    });
    return m;
  }, [materials]);

  const brandItemsByMaterialCode = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(brandItems) ? brandItems : [];
    list.forEach((bi) => {
      const code = String(bi?.materialCode || bi?.material_code || '').trim();
      if (!code) return;
      if (!m.has(code)) m.set(code, []);
      m.get(code).push(bi);
    });
    for (const [code, items] of m.entries()) {
      const filtered = (items || []).filter((x) => String(x?.status || 'Activo') === 'Activo');
      filtered.sort((a, b) => String(a?.name || a?.brand || '').localeCompare(String(b?.name || b?.brand || '')) || String(a?.serial || '').localeCompare(String(b?.serial || '')));
      m.set(code, filtered);
    }
    return m;
  }, [brandItems]);

  const getTechAssignmentsForDoc = (doc) => {
    const dniKey = normalizePersonDoc(doc);
    if (!dniKey) return [];
    const rows = Array.isArray(assignments) ? assignments : [];
    const tech = rows.filter((a) => {
      const aDoc = normalizePersonDoc(a?.responsibleDoc);
      if (!aDoc || aDoc !== dniKey) return false;
      const catA = normalizeText(a?.materialCategory || '');
      if (catA) return catA.includes('tecn');
      const mid = a?.materialId;
      if (mid === null || mid === undefined) return false;
      const mat = materialById.get(String(mid));
      const cat = normalizeText(mat?.category || mat?.type || '');
      return cat.includes('tecn');
    });
    tech.sort((x, y) => String(x.materialName || '').localeCompare(String(y.materialName || '')));
    return tech;
  };

  const inferAssetTypeFromMaterial = (mName, mCode) => {
    const t = normalizeText(`${mName || ''} ${mCode || ''}`);
    if (/(portatil|portatil|laptop|notebook)/.test(t)) return 'Portátil';
    if (/monitor/.test(t)) return 'Monitor';
    if (/(impresora|printer)/.test(t)) return 'Impresora';
    if (/(pc|cpu|torre|desktop)/.test(t)) return 'PC';
    return 'Periférico';
  };

  // eslint-disable-next-line no-unused-vars
  const importTechAssignmentsIntoEquipments = (doc) => {
    const tech = getTechAssignmentsForDoc(doc);
    if (tech.length === 0) return;
    const mapped = tech.map((a) => ({
      asset_type: inferAssetTypeFromMaterial(a.materialName, a.materialCode),
      serial: String(a.serial || '').trim(),
      asset_tag: '',
      material_code: a.materialCode || '',
      brand: a.brandName || '',
      model: '',
      hostname: '',
      inventory_number: '',
      estado_equipo: a.status || 'Activo',
      sistema_operativo: '',
      accesorios: '',
      status: 'Activo',
      purchase_date: '',
      warranty_until: '',
      notes: '',
    }));
    const nonEmpty = mapped.filter((e) => String(e.serial || '').trim() !== '' || String(e.material_code || '').trim() !== '' || String(e.brand || '').trim() !== '');
    if (nonEmpty.length === 0) return;
    setHvEditing((prev) => {
      const p = prev || {};
      const current = Array.isArray(p.equipments) ? p.equipments : [];
      const isBlank = (eq) => {
        const keys = ['serial','brand','model','hostname','material_code','asset_tag','inventory_number'];
        return keys.every((k) => !String(eq?.[k] || '').trim());
      };
      const allBlank = current.length === 0 || current.every(isBlank);
      if (!allBlank) return p;
      return { ...p, equipments: nonEmpty };
    });
  };

  const handleHvFuncionarioDocChange = (value) => {
    const v = String(value ?? '');
    const found = findPersonByDoc(v);
    setHvEditing((prev) => {
      const p = prev || {};
      const next = { ...p, assigned_to_dni: v };
      if (found) {
        next.assigned_to_name = (found.name || found.nombre || found.nombre_completo || '');
        next.funcionario_cargo = found.position || found.role || '';
        next.dependencia_area = found.area || '';
        next.correo_institucional = found.email || '';
        next.ext_telefono = found.phone || '';
      }
      return next;
    });
  };

  const openCreateHv = () => {
    setHvEditing({
      assigned_to_name: '',
      assigned_to_dni: '',
      document_type: '',
      funcionario_cargo: '',
      dependencia_area: '',
      centro_costos: '',
      correo_institucional: '',
      ext_telefono: '',
      sede_ubicacion: '',
      assignment_date: '',
      assignment_reason: '',
      software_licenses: [{ name: '', license: '', expiry: '', notes: '' }],
      general_notes: '',
      diagnostic_protocol: {},
      inspection_type: '',
      inspection_date: '',
      inspection_observation: '',
      assigned_to_admin_id: '',
      location: '',
      equipments: [{
        asset_type: 'Portátil',
        serial: '',
        asset_tag: '',
        material_code: '',
        sede_ubicacion: '',
        _showMore: false,
        brand: '',
        model: '',
        hostname: '',
        inventory_number: '',
        estado_equipo: '',
        sistema_operativo: '',
        accesorios: '',
        status: 'Activo',
        purchase_date: '',
        warranty_until: '',
        notes: '',
      }],
    });
    setHvCreateOpen(true);
  };

  const hvCreateAddEquipment = () => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.equipments) ? p.equipments : [];
      return {
        ...p,
        equipments: [...list, {
          asset_type: 'Portátil',
          serial: '',
          asset_tag: '',
          material_code: '',
          sede_ubicacion: '',
          _showMore: false,
          brand: '',
          model: '',
          hostname: '',
          inventory_number: '',
          estado_equipo: '',
          sistema_operativo: '',
          accesorios: '',
          status: 'Activo',
          purchase_date: '',
          warranty_until: '',
          notes: '',
        }],
      };
    });
  };

  const hvCreateRemoveEquipment = (idx) => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.equipments) ? p.equipments : [];
      if (list.length <= 1) return p;
      return { ...p, equipments: list.filter((_, i) => i !== idx) };
    });
  };

  const hvCreateUpdateEquipment = (idx, patch) => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.equipments) ? p.equipments : [];
      return { ...p, equipments: list.map((it, i) => (i === idx ? { ...it, ...patch } : it)) };
    });
  };

  const hvCreateAddLicense = () => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.software_licenses) ? p.software_licenses : [];
      return { ...p, software_licenses: [...list, { name: '', license: '', expiry: '', notes: '' }] };
    });
  };

  const hvCreateRemoveLicense = (idx) => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.software_licenses) ? p.software_licenses : [];
      if (list.length <= 1) return p;
      return { ...p, software_licenses: list.filter((_, i) => i !== idx) };
    });
  };

  const hvCreateUpdateLicense = (idx, patch) => {
    setHvEditing((prev) => {
      const p = prev || {};
      const list = Array.isArray(p.software_licenses) ? p.software_licenses : [];
      return { ...p, software_licenses: list.map((it, i) => (i === idx ? { ...it, ...patch } : it)) };
    });
  };

  const openEditHv = (row) => {
    const code = String(row?.material_code || '').trim();
    const serial = String(row?.serial || '').trim();
    const list = code ? (brandItemsByMaterialCode.get(code) || []) : [];
    const exists = code && serial ? list.some((x) => String(x?.serial || '').trim() === serial) : false;
    const licenses = (() => {
      const raw = row?.software_licenses_json || row?.software_licenses || null;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        } catch (_) {}
      }
      const one = {
        name: row?.software_name || '',
        license: row?.software_license || '',
        expiry: row?.software_expiry || '',
        notes: row?.software_notes || '',
      };
      if (String(one.name || '').trim() || String(one.license || '').trim() || String(one.expiry || '').trim() || String(one.notes || '').trim()) return [one];
      return [{ name: '', license: '', expiry: '', notes: '' }];
    })();
    setHvEditing({
      id: row?.id,
      assigned_to_dni: row?.assigned_to_dni || '',
      assigned_to_name: row?.assigned_to_name || '',
      assigned_to_admin_id: row?.assigned_to_admin_id ?? '',
      document_type: row?.document_type || '',
      funcionario_cargo: row?.funcionario_cargo || '',
      dependencia_area: row?.dependencia_area || '',
      centro_costos: row?.centro_costos || '',
      correo_institucional: row?.correo_institucional || '',
      ext_telefono: row?.ext_telefono || '',
      sede_ubicacion: row?.sede_ubicacion || '',
      assignment_date: row?.assignment_date || '',
      assignment_reason: row?.assignment_reason || '',
      software_licenses: licenses,
      general_notes: row?.general_notes || '',
      diagnostic_protocol: (() => {
        const raw = row?.diagnostic_protocol_json || row?.diagnostic_protocol || null;
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
          } catch (_) {}
        }
        return {};
      })(),
      inspection_type: row?.inspection_type || '',
      inspection_date: row?.inspection_date || '',
      inspection_observation: row?.inspection_observation || '',
      location: row?.location || '',
      equipments: [{
        id: row?.id,
        asset_type: row?.asset_type || 'Portátil',
        material_code: code,
        sede_ubicacion: row?.sede_ubicacion || '',
        _showMore: false,
        _brandItemKey: exists ? `${code}||${serial}` : '',
        brand: row?.brand || '',
        model: row?.model || '',
        serial: serial,
        status: row?.status || 'Activo',
        sistema_operativo: row?.sistema_operativo || '',
        accesorios: row?.accesorios || '',
      }]
    });
    setHvEditOpen(false);
    setHvCreateOpen(true);
  };

  const saveHv = async () => {
    const s = hvEditing || {};
    setHvSaving(true);
    try {
      const adminId = (s.assigned_to_admin_id === '' || s.assigned_to_admin_id === null || s.assigned_to_admin_id === undefined)
        ? null
        : Number(s.assigned_to_admin_id);

      // Modo formato (múltiples equipos)
      if (hvCreateOpen && Array.isArray(s.equipments)) {
        const eqs = s.equipments || [];
        if (eqs.length === 0) throw new Error('Debe agregar al menos un equipo');
        const licList = Array.isArray(s.software_licenses) ? s.software_licenses : [];
        const licFirst = licList[0] || {};
        for (const eq of eqs) {
          const serialEq = String(eq?.serial || '').trim();
          const typeEq = String(eq?.asset_type || '').trim();
          if (!serialEq || !typeEq) {
            throw new Error('Cada equipo debe tener Tipo de equipo y N° de serie');
          }
          const payload = {
            id: eq?.id ?? undefined,
            asset_type: typeEq,
            serial: serialEq,
            asset_tag: String(eq?.asset_tag || '').trim() || null,
            material_code: String(eq?.material_code || '').trim() || null,
            brand: String(eq?.brand || '').trim() || null,
            model: String(eq?.model || '').trim() || null,
            hostname: String(eq?.hostname || '').trim() || null,
            inventory_number: String(eq?.inventory_number || '').trim() || null,
            estado_equipo: String(eq?.estado_equipo || '').trim() || null,
            sistema_operativo: String(eq?.sistema_operativo || '').trim() || null,
            accesorios: String(eq?.accesorios || '').trim() || null,
            assigned_to_dni: String(s.assigned_to_dni || '').trim() || null,
            assigned_to_name: String(s.assigned_to_name || '').trim() || null,
            assigned_to_admin_id: Number.isFinite(adminId) ? adminId : null,
            changed_by_admin_id: currentUser?.id ?? null,
            document_type: String(s.document_type || '').trim() || null,
            funcionario_cargo: String(s.funcionario_cargo || '').trim() || null,
            dependencia_area: String(s.dependencia_area || '').trim() || null,
            centro_costos: String(s.centro_costos || '').trim() || null,
            correo_institucional: String(s.correo_institucional || '').trim() || null,
            ext_telefono: String(s.ext_telefono || '').trim() || null,
            sede_ubicacion: String(eq?.sede_ubicacion || s.sede_ubicacion || '').trim() || null,
            assignment_type: null,
            assignment_date: String(s.assignment_date || '').trim() || null,
            assignment_reason: String(s.assignment_reason || '').trim() || null,
            software_name: String(licFirst?.name || '').trim() || null,
            software_license: String(licFirst?.license || '').trim() || null,
            software_expiry: String(licFirst?.expiry || '').trim() || null,
            software_notes: String(licFirst?.notes || '').trim() || null,
            software_licenses: licList,
            general_notes: String(s.general_notes || '').trim() || null,
            diagnostic_protocol: (s.diagnostic_protocol && typeof s.diagnostic_protocol === 'object') ? s.diagnostic_protocol : null,
            inspection_type: String(s.inspection_type || '').trim() || null,
            inspection_date: String(s.inspection_date || '').trim() || null,
            inspection_observation: String(s.inspection_observation || '').trim() || null,
            location: String(s.location || eq?.sede_ubicacion || s.sede_ubicacion || '').trim() || null,
            status: String(eq?.status || 'Activo').trim(),
            purchase_date: String(eq?.purchase_date || '').trim() || null,
            warranty_until: String(eq?.warranty_until || '').trim() || null,
            notes: String(eq?.notes || '').trim() || null,
          };
          const r = await api.admin.hvAssets.upsert(payload);
          if (!r?.ok) throw new Error(r?.error || `No fue posible guardar el equipo con serial ${serialEq}`);
        }
        setStatusMsg({ type: 'success', text: 'Hoja de vida guardada' });
      } else {
        // Modo edición individual
        const serial = String(s.serial || '').trim();
        const asset_type = String(s.asset_type || '').trim();
        if (!asset_type || !serial) {
          throw new Error('Campos requeridos: Tipo y Serial');
        }
        const licList = Array.isArray(s.software_licenses) ? s.software_licenses : [];
        const licFirst = licList[0] || {};
        const payload = {
          id: s.id ?? undefined,
          asset_type,
          serial,
          asset_tag: String(s.asset_tag || '').trim() || null,
          material_code: String(s.material_code || '').trim() || null,
          brand: String(s.brand || '').trim() || null,
          model: String(s.model || '').trim() || null,
          hostname: String(s.hostname || '').trim() || null,
          assigned_to_dni: String(s.assigned_to_dni || '').trim() || null,
          assigned_to_name: String(s.assigned_to_name || '').trim() || null,
          assigned_to_admin_id: Number.isFinite(adminId) ? adminId : null,
          changed_by_admin_id: currentUser?.id ?? null,
          document_type: String(s.document_type || '').trim() || null,
          funcionario_cargo: String(s.funcionario_cargo || '').trim() || null,
          dependencia_area: String(s.dependencia_area || '').trim() || null,
          centro_costos: String(s.centro_costos || '').trim() || null,
          correo_institucional: String(s.correo_institucional || '').trim() || null,
          ext_telefono: String(s.ext_telefono || '').trim() || null,
          sede_ubicacion: String(s.sede_ubicacion || '').trim() || null,
          inventory_number: String(s.inventory_number || '').trim() || null,
          estado_equipo: String(s.estado_equipo || '').trim() || null,
          sistema_operativo: String(s.sistema_operativo || '').trim() || null,
          accesorios: String(s.accesorios || '').trim() || null,
          assignment_type: null,
          assignment_date: String(s.assignment_date || '').trim() || null,
          assignment_reason: String(s.assignment_reason || '').trim() || null,
          software_name: String(licFirst?.name || '').trim() || null,
          software_license: String(licFirst?.license || '').trim() || null,
          software_expiry: String(licFirst?.expiry || '').trim() || null,
          software_notes: String(licFirst?.notes || '').trim() || null,
          software_licenses: licList,
          general_notes: String(s.general_notes || '').trim() || null,
          diagnostic_protocol: (s.diagnostic_protocol && typeof s.diagnostic_protocol === 'object') ? s.diagnostic_protocol : null,
          inspection_type: String(s.inspection_type || '').trim() || null,
          inspection_date: String(s.inspection_date || '').trim() || null,
          inspection_observation: String(s.inspection_observation || '').trim() || null,
          location: String(s.location || '').trim() || null,
          status: String(s.status || 'Activo').trim(),
          purchase_date: String(s.purchase_date || '').trim() || null,
          warranty_until: String(s.warranty_until || '').trim() || null,
          notes: String(s.notes || '').trim() || null,
        };
        const r = await api.admin.hvAssets.upsert(payload);
        if (!r?.ok) throw new Error(r?.error || 'No fue posible guardar el activo');
        setStatusMsg({ type: 'success', text: 'Activo guardado' });
      }

      setHvCreateOpen(false);
      setHvEditOpen(false);
      setHvEditing(null);
      loadHvAssets();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error guardando activo' });
    } finally {
      setHvSaving(false);
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const deleteHv = async (row) => {
    const id = row?.id;
    if (!id) return;
    const ok = window.confirm(`¿Eliminar este activo?\n\n${String(row?.asset_type || '')} — ${String(row?.serial || '')}`);
    if (!ok) return;
    try {
      const r = await api.admin.hvAssets.delete({ id });
      if (!r?.ok) throw new Error(r?.error || 'No fue posible eliminar el activo');
      setStatusMsg({ type: 'success', text: 'Activo eliminado' });
      loadHvAssets();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error eliminando activo' });
    } finally {
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const printHvFormat = async (row) => {
    if (!row) return;
    try {
      let asset = row;
      if (row?.id) {
        const res = await api.admin.hvAssets.list({ id: row.id, include_deleted: 1, limit: 1 });
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : (res?.data?.data ? res.data.data : []));
        if (Array.isArray(rows) && rows[0]) asset = rows[0];
      }
      const serial = String(asset?.serial || '').trim();
      let history = [];
      if (serial) {
        try {
          const hres = await api.admin.hvAssets.list({ action: 'responsible_history', serial, limit: 50 });
          history = Array.isArray(hres?.data) ? hres.data : [];
        } catch (_) {}
      }
      await printHvActivo(asset, { history });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error imprimiendo formato' });
      setTimeout(() => setStatusMsg(null), 2500);
    }
  };

  const loadHvResponsibleHistory = async (serial) => {
    const s = String(serial || '').trim();
    if (!s) return;
    try {
      const res = await api.admin.hvAssets.list({ action: 'responsible_history', serial: s, limit: 50 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setHvResponsibleHistory((prev) => ({ ...(prev || {}), [s]: rows }));
    } catch (_) {
      setHvResponsibleHistory((prev) => ({ ...(prev || {}), [s]: [] }));
    }
  };

  const loadHvInspectionHistory = async (serial) => {
    const s = String(serial || '').trim();
    if (!s) return;
    setHvInspectionLoading(true);
    try {
      const res = await api.admin.hvInspections.list({ serial: s, limit: 200 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setHvInspectionFiles(rows);
    } catch (_) {
      setHvInspectionFiles([]);
    } finally {
      setHvInspectionLoading(false);
    }
  };

  const openHvInspectionHistory = (serial) => {
    const s = String(serial || '').trim();
    if (!s) return;
    setHvInspectionSerial(s);
    setHvInspectionModalOpen(true);
    loadHvInspectionHistory(s);
  };

  const saveHvInspectionBackup = async () => {
    const s = hvEditing || {};
    const eqs = Array.isArray(s.equipments) ? s.equipments : [];
    const serials = eqs.map((e) => String(e?.serial || '').trim()).filter(Boolean);
    const uniqueSerials = serials.filter((v, i, arr) => arr.indexOf(v) === i);
    if (!uniqueSerials.length) {
      setStatusMsg({ type: 'error', text: 'Debe tener al menos un equipo con serial para guardar inspección' });
      setTimeout(() => setStatusMsg(null), 2500);
      return;
    }
    setHvInspectionSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      for (const serial of uniqueSerials) {
        const eq = eqs.find((e) => String(e?.serial || '').trim() === serial) || {};
        let history = [];
        try {
          const hres = await api.admin.hvAssets.list({ action: 'responsible_history', serial, limit: 50 });
          history = Array.isArray(hres?.data) ? hres.data : [];
        } catch (_) {}
        const asset = {
          id: eq?.id ?? s.id,
          serial,
          asset_type: eq?.asset_type || s.asset_type || '',
          material_code: eq?.material_code || s.material_code || '',
          sede_ubicacion: eq?.sede_ubicacion || s.sede_ubicacion || '',
          brand: eq?.brand || s.brand || '',
          model: eq?.model || s.model || '',
          status: eq?.status || s.status || 'Activo',
          sistema_operativo: eq?.sistema_operativo || s.sistema_operativo || '',
          accesorios: eq?.accesorios || s.accesorios || '',
          assigned_to_name: s.assigned_to_name || '',
          assigned_to_dni: s.assigned_to_dni || '',
          funcionario_cargo: s.funcionario_cargo || '',
          dependencia_area: s.dependencia_area || '',
          assignment_date: s.assignment_date || '',
          assignment_reason: s.assignment_reason || '',
          inspection_type: s.inspection_type || '',
          inspection_date: s.inspection_date || '',
          inspection_observation: s.inspection_observation || '',
          general_notes: s.general_notes || '',
          software_licenses: Array.isArray(s.software_licenses) ? s.software_licenses : [],
          diagnostic_protocol: (s.diagnostic_protocol && typeof s.diagnostic_protocol === 'object') ? s.diagnostic_protocol : {},
        };
        const { blob } = await generateHvActivoPdfBlob(asset, { history });
        const file = new File([blob], `HV-${serial}-${today}.pdf`, { type: 'application/pdf' });
        const fd = new FormData();
        fd.append('serial', serial);
        fd.append('file', file);
        fd.append('created_by_admin_id', String(currentUser?.id ?? ''));
        await api.admin.hvInspections.upload(fd);
      }
      if (uniqueSerials.length) {
        setHvInspectionSerial(uniqueSerials[0]);
        setHvInspectionModalOpen(true);
        loadHvInspectionHistory(uniqueSerials[0]);
      }
      setStatusMsg({ type: 'success', text: 'Inspección guardada en historial' });
      setTimeout(() => setStatusMsg(null), 2500);
      if (hvInspectionModalOpen && hvInspectionSerial) {
        loadHvInspectionHistory(hvInspectionSerial);
      }
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'Error guardando inspección' });
      setTimeout(() => setStatusMsg(null), 2500);
    } finally {
      setHvInspectionSaving(false);
    }
  };

  useEffect(() => {
    if (!hvCreateOpen) return;
    const serials = (Array.isArray(hvEditing?.equipments) ? hvEditing.equipments : [])
      .map((e) => String(e?.serial || '').trim())
      .filter((s, i, arr) => s && arr.indexOf(s) === i);
    serials.forEach((s) => {
      if (hvResponsibleHistory[s]) return;
      loadHvResponsibleHistory(s);
    });
  }, [hvCreateOpen, hvEditing?.equipments, hvResponsibleHistory]);

  return (
    <div className="text-gray-800">
      {statusMsg && (
        <div className={`fixed top-4 right-4 z-50 px-3 py-2 text-sm rounded-md shadow border ${statusMsg.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
          {statusMsg.text}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-semibold">Administración TI</h2>
      </div>
      {showLocation && (
        <p className="text-gray-700">Sede: {title}</p>
      )}

      <div className="grid grid-cols-1 gap-6 mt-4">
        <SectionIndexCards
          title="Indice de secciones"
          helperText="Gestiona usuarios, soporte, activos, auditoría y control documental de TI."
          columnsClassName="md:grid-cols-2 xl:grid-cols-3"
          items={[
            ...(canManageUsersAndRoles ? [{
              key: 'usuarios',
              title: 'Usuarios y roles',
              description: 'Permisos, accesos y administracion del sistema',
              icon: Users,
              gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
              accentGradient: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)',
              active: adminTiView === 'usuarios',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('usuarios'); setView('permisos'); },
            }] : []),
            {
              key: 'soportes',
              title: 'Mesa de ayuda',
              description: 'Tickets, seguimiento y priorizacion de soporte',
              icon: Headset,
              gradient: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
              accentGradient: 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)',
              active: adminTiView === 'soportes',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('soportes'); },
            },
            {
              key: 'hv-activos',
              title: 'HV activos',
              description: 'Hoja de vida, diagnostico y control de equipos',
              icon: MonitorSmartphone,
              gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)',
              accentGradient: 'linear-gradient(180deg, #F97316 0%, #EA580C 100%)',
              active: adminTiView === 'hv-activos',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('hv-activos'); },
            },
            {
              key: 'bitacora-cambios',
              title: 'Bitacora de cambios',
              description: 'Registro historico de ajustes y despliegues',
              icon: ScrollText,
              gradient: 'linear-gradient(135deg, #16A34A 0%, #84CC16 100%)',
              accentGradient: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)',
              active: adminTiView === 'bitacora-cambios',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('bitacora-cambios'); },
            },
            ...(canViewAuditSection ? [{
              key: 'auditoria',
              title: 'Auditoría',
              description: 'Historial de accesos y actividad de usuarios',
              icon: ShieldCheck,
              gradient: 'linear-gradient(135deg, #0F766E 0%, #0EA5E9 100%)',
              accentGradient: 'linear-gradient(180deg, #22D3EE 0%, #0F766E 100%)',
              active: adminTiView === 'auditoria',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('auditoria'); },
            }] : []),
            ...(canViewAuditSection ? [{
              key: 'registro-operaciones',
              title: 'Registro de operaciones',
              description: 'Operaciones de negocio y descargas de usuarios',
              icon: ClipboardList,
              gradient: 'linear-gradient(135deg, #1D4ED8 0%, #6366F1 100%)',
              accentGradient: 'linear-gradient(180deg, #818CF8 0%, #1D4ED8 100%)',
              active: adminTiView === 'registro-operaciones',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('registro-operaciones'); },
            }] : []),
            {
              key: 'control-versiones-calidad',
              title: 'Control versiones calidad',
              description: 'Seguimiento documental y control de versiones',
              icon: ShieldCheck,
              gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
              accentGradient: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
              active: adminTiView === 'control-versiones-calidad',
              onClick: () => { setAdminTiGroup('gestion'); setAdminTiView('control-versiones-calidad'); },
            },
          ]}
        />

        {/* Gestión de usuarios y roles */}
        {adminTiView === 'usuarios' && canManageUsersAndRoles && (
          <AdministracionUsuariosYRoles location={location} />
        )}

        {adminTiView === 'usuarios' && !canManageUsersAndRoles && (
        <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            La administración de usuarios, roles y permisos solo está disponible para `admin@senerfi.com`.
          </div>
        </div>
        )}

        {adminTiView === 'bitacora-cambios' && (
        <div className="rounded-2xl p-[1px] shadow-lg bg-gradient-to-br from-slate-100 via-white to-blue-50">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bitácora de Cambios</h3>
                <p className="mt-1 text-sm text-gray-600">Registro de cambios realizados al sistema.</p>
              </div>
              <div className="text-xs text-gray-600 flex items-center gap-4">
                <div><span className="font-semibold">{bitacoraTotal || 0}</span> registros</div>
                <div>Última versión: <span className="font-semibold">{bitacoraLastVersion || '-'}</span></div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  {bitacoraEditId ? 'Editar registro' : 'Nuevo registro'}
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Versión</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={bitacoraForm.version}
                        onChange={(e) => setBitacoraForm((p) => ({ ...p, version: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha</label>
                      <input
                        type="date"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={bitacoraForm.fecha}
                        onChange={(e) => setBitacoraForm((p) => ({ ...p, fecha: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Título del cambio</label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      value={bitacoraForm.titulo}
                      onChange={(e) => setBitacoraForm((p) => ({ ...p, titulo: e.target.value }))}
                      placeholder="Resumen del cambio realizado"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Descripción</label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 text-sm min-h-28"
                      value={bitacoraForm.descripcion}
                      onChange={(e) => setBitacoraForm((p) => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Detalla qué se modificó, por qué y el impacto esperado"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Módulo / componente</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={bitacoraForm.modulo_componente}
                        onChange={(e) => setBitacoraForm((p) => ({ ...p, modulo_componente: e.target.value }))}
                        placeholder="ej. API, BD, UI..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Responsable</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={bitacoraForm.responsable}
                        onChange={(e) => setBitacoraForm((p) => ({ ...p, responsable: e.target.value }))}
                        placeholder="Nombre o equipo"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-2">Tipo de cambio</div>
                    <div className="grid grid-cols-2 gap-2">
                      {BITACORA_TIPOS.map((t) => (
                        <button
                          key={`tipo-${t}`}
                          type="button"
                          onClick={() => setBitacoraForm((p) => ({ ...p, tipo_cambio: t }))}
                          className={`px-3 py-2 rounded-md border text-sm text-left ${bitacoraForm.tipo_cambio === t ? 'bg-green-50 border-green-200 text-green-700 font-semibold' : 'bg-white hover:bg-gray-50'}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Estado</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                      value={bitacoraForm.estado}
                      onChange={(e) => setBitacoraForm((p) => ({ ...p, estado: e.target.value }))}
                    >
                      {BITACORA_ESTADOS.map((s) => (
                        <option key={`est-${s}`} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-2">Prioridad</div>
                    <div className="grid grid-cols-3 gap-2">
                      {BITACORA_PRIORIDADES.map((p) => (
                        <button
                          key={`prio-${p}`}
                          type="button"
                          onClick={() => setBitacoraForm((x) => ({ ...x, prioridad: p }))}
                          className={`px-3 py-2 rounded-md border text-sm ${bitacoraForm.prioridad === p ? (p === 'Alta' ? 'bg-red-50 border-red-200 text-red-700 font-semibold' : (p === 'Media' ? 'bg-yellow-50 border-yellow-200 text-yellow-700 font-semibold' : 'bg-gray-100 border-gray-200 text-gray-700 font-semibold')) : 'bg-white hover:bg-gray-50'}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={saveBitacora}
                      disabled={bitacoraSaving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-white text-sm font-semibold"
                      style={{ backgroundColor: bitacoraSaving ? '#FBBF24' : '#D97706' }}
                    >
                      <Save size={16} />
                      {bitacoraEditId ? (bitacoraSaving ? 'Guardando...' : 'Guardar cambios') : (bitacoraSaving ? 'Guardando...' : 'Agregar a la bitácora')}
                    </button>
                    {bitacoraEditId ? (
                      <button
                        type="button"
                        onClick={resetBitacoraForm}
                        className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
                      >
                        Cancelar
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">TOTAL</div>
                    <div className="text-2xl font-semibold">{bitacoraRows.length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">NUEVAS FUNCIONES</div>
                    <div className="text-2xl font-semibold text-green-600">{bitacoraRows.filter((r) => String(r?.tipo_cambio) === 'Nueva función').length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">MEJORAS</div>
                    <div className="text-2xl font-semibold text-blue-600">{bitacoraRows.filter((r) => String(r?.tipo_cambio) === 'Mejora').length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">CORRECCIONES</div>
                    <div className="text-2xl font-semibold text-red-600">{bitacoraRows.filter((r) => String(r?.tipo_cambio) === 'Corrección').length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">SEGURIDAD</div>
                    <div className="text-2xl font-semibold text-purple-600">{bitacoraRows.filter((r) => String(r?.tipo_cambio) === 'Seguridad').length}</div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setBitacoraTipo('Todos')}
                      className={`px-3 py-1.5 rounded-full text-xs border ${bitacoraTipo === 'Todos' ? 'bg-gray-100 font-semibold' : 'bg-white hover:bg-gray-50'}`}
                    >
                      Todos
                    </button>
                    {BITACORA_TIPOS.map((t) => (
                      <button
                        key={`flt-${t}`}
                        type="button"
                        onClick={() => setBitacoraTipo(t)}
                        className={`px-3 py-1.5 rounded-full text-xs border ${bitacoraTipo === t ? 'bg-gray-100 font-semibold' : 'bg-white hover:bg-gray-50'}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="border rounded-full px-4 py-2 text-sm w-full md:w-72"
                      placeholder="Buscar..."
                      value={bitacoraQuery}
                      onChange={(e) => setBitacoraQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-4 border rounded-xl p-4 min-h-[340px] bg-gray-50">
                  {bitacoraLoading ? (
                    <div className="text-sm text-gray-600">Cargando...</div>
                  ) : bitacoraRows.length === 0 ? (
                    <div className="text-sm text-gray-500 text-center py-16">
                      <div className="font-semibold">No hay registros aún.</div>
                      <div className="mt-1">Usa el formulario para agregar el primer cambio.</div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bitacoraRows.map((r) => (
                        <div key={`bc-${r.id}`} className="bg-white border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-xs text-gray-500">{r.fecha || '-'}</div>
                                <div className="text-xs font-semibold text-gray-700">{r.version || '-'}</div>
                                {r.tipo_cambio ? (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700">{r.tipo_cambio}</span>
                                ) : null}
                                {r.estado ? (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white text-gray-700">{r.estado}</span>
                                ) : null}
                                {r.prioridad ? (
                                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${String(r.prioridad) === 'Alta' ? 'bg-red-50 border-red-200 text-red-700' : (String(r.prioridad) === 'Media' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-100 border-gray-200 text-gray-700')}`}>{r.prioridad}</span>
                                ) : null}
                              </div>
                              <div className="mt-1 font-semibold text-gray-900 break-words">{r.titulo || '-'}</div>
                              {r.descripcion ? (
                                <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">{r.descripcion}</div>
                              ) : null}
                              <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                                {r.modulo_componente ? <div>Módulo: <span className="font-semibold">{r.modulo_componente}</span></div> : null}
                                {r.responsable ? <div>Responsable: <span className="font-semibold">{r.responsable}</span></div> : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="px-2 py-2 rounded-md border bg-white hover:bg-gray-50"
                                onClick={() => {
                                  setBitacoraEditId(r.id);
                                  setBitacoraForm({
                                    version: r.version || '',
                                    fecha: r.fecha || '',
                                    titulo: r.titulo || '',
                                    descripcion: r.descripcion || '',
                                    modulo_componente: r.modulo_componente || '',
                                    responsable: r.responsable || '',
                                    tipo_cambio: r.tipo_cambio || 'Nueva función',
                                    estado: r.estado || 'Implementado',
                                    prioridad: r.prioridad || 'Media',
                                  });
                                }}
                                title="Editar"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                type="button"
                                className="px-2 py-2 rounded-md border bg-white hover:bg-gray-50 text-red-600"
                                onClick={() => deleteBitacora(r)}
                                title="Eliminar"
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {adminTiView === 'auditoria' && !canViewAuditSection && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
            No tiene permiso para consultar la auditoría (sección Administración TI → Auditoría).
          </div>
        )}

        {adminTiView === 'registro-operaciones' && !canViewAuditSection && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
            No tiene permiso para consultar el registro de operaciones.
          </div>
        )}

        {adminTiView === 'registro-operaciones' && canViewAuditSection && (
          <AdminOperationsLogView />
        )}

        {adminTiView === 'auditoria' && canViewAuditSection && (
        <div className="rounded-2xl p-[1px] shadow-lg bg-gradient-to-br from-cyan-100 via-white to-teal-50">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Auditoría de usuarios</h3>
                <p className="mt-1 text-sm text-gray-600">Consulta de solo lectura: usuario, fecha-hora, módulo y tipo de operación (incluye mutaciones de negocio con antes/después).</p>
              </div>
              <button
                type="button"
                onClick={loadAuditRows}
                className="px-3 py-2 rounded-lg text-sm text-white shadow-sm hover:opacity-95"
                style={{ backgroundColor: '#0F766E' }}
              >
                Actualizar historial
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-gray-500">Eventos consultados</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{auditStats.total}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-gray-500">Ingresos exitosos</div>
                <div className="mt-1 text-2xl font-semibold text-emerald-600">{auditStats.loginCount}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-gray-500">Usuarios visibles</div>
                <div className="mt-1 text-2xl font-semibold text-cyan-700">{auditStats.uniqueUsers}</div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={auditQuery}
                  onChange={(e) => setAuditQuery(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  placeholder="Buscar usuario, acción o endpoint"
                />
                <select
                  value={auditUserId}
                  onChange={(e) => setAuditUserId(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="Todos">Todos los usuarios</option>
                  {users.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.name || user.email}
                    </option>
                  ))}
                </select>
                <select
                  value={auditModule}
                  onChange={(e) => setAuditModule(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="Todos">Todos los módulos</option>
                  {auditModuleOptions.map((moduleKey) => (
                    <option key={moduleKey} value={moduleKey}>
                      {AUDIT_MODULE_LABELS[moduleKey] || moduleKey}
                    </option>
                  ))}
                </select>
                <select
                  value={auditEventType}
                  onChange={(e) => setAuditEventType(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="Todos">Todos los tipos</option>
                  {auditEventTypeOptions.map((ev) => (
                    <option key={ev} value={ev}>
                      {AUDIT_EVENT_LABELS[ev] || ev}
                    </option>
                  ))}
                </select>
                <input
                  type="datetime-local"
                  value={auditDateTimeFrom}
                  onChange={(e) => setAuditDateTimeFrom(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  title="Desde (fecha y hora)"
                />
                <input
                  type="datetime-local"
                  value={auditDateTimeTo}
                  onChange={(e) => setAuditDateTimeTo(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  title="Hasta (fecha y hora)"
                />
                <input
                  type="date"
                  value={auditDateFrom}
                  onChange={(e) => setAuditDateFrom(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  title="Desde (solo día, si no usa hora)"
                />
                <input
                  type="date"
                  value={auditDateTo}
                  onChange={(e) => setAuditDateTo(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                  title="Hasta (solo día, si no usa hora)"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={loadAuditRows}
                  className="px-3 py-2 rounded-lg text-sm text-white shadow-sm hover:opacity-95"
                  style={{ backgroundColor: '#F07E2D' }}
                >
                  Aplicar filtros
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAuditQuery('');
                    setAuditUserId('Todos');
                    setAuditModule('Todos');
                    setAuditEventType('Todos');
                    setAuditDateFrom('');
                    setAuditDateTo('');
                    setAuditDateTimeFrom('');
                    setAuditDateTimeTo('');
                  }}
                  className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAuditView('tabla')}
                className={`px-3 py-1.5 rounded-full border text-sm ${auditView === 'tabla' ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                style={auditView === 'tabla' ? { backgroundColor: '#0F766E' } : undefined}
              >
                Tabla
              </button>
              <button
                type="button"
                onClick={() => setAuditView('timeline')}
                className={`px-3 py-1.5 rounded-full border text-sm ${auditView === 'timeline' ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                style={auditView === 'timeline' ? { backgroundColor: '#0F766E' } : undefined}
              >
                Línea de tiempo por usuario
              </button>
            </div>

            {auditView === 'timeline' ? (
              <div className="mt-3 space-y-4">
                {auditLoading && (
                  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-gray-500 text-sm">Cargando historial…</div>
                )}
                {!auditLoading && auditTimelineGroups.length === 0 && (
                  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-gray-500 text-sm">No hay eventos de auditoría para los filtros seleccionados.</div>
                )}
                {!auditLoading && auditTimelineGroups.map((group) => (
                  <div key={group.key} className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="font-medium text-gray-900">{group.name}</div>
                        <div className="text-xs text-gray-500">{group.email || 'Sin correo'}</div>
                      </div>
                      <div className="text-xs text-gray-500">{group.totalEvents} evento{group.totalEvents === 1 ? '' : 's'} · {group.sessions.length} sesión{group.sessions.length === 1 ? '' : 'es'}</div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {group.sessions.map((session, sIdx) => (
                        <div key={sIdx} className="px-4 py-3">
                          {session.loginRow ? (
                            <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                              Inicio de sesión — {formatAuditDateTime(session.loginRow.created_at)}
                              {session.loginRow.ip_address ? <span className="text-xs text-gray-400 font-normal">· {session.loginRow.ip_address}</span> : null}
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-gray-500">Actividad sin inicio de sesión registrado en este rango</div>
                          )}
                          {session.events.length === 0 ? (
                            <div className="mt-2 ml-4 text-xs text-gray-400">Sin acciones registradas después del ingreso.</div>
                          ) : (
                            <ol className="mt-2 ml-4 border-l-2 border-gray-100 space-y-2">
                              {session.events.map((row) => (
                                <li key={row.id} className="pl-3 relative">
                                  <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-cyan-400" />
                                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                                    <span className="font-mono text-xs text-gray-500">{formatAuditDateTime(row.created_at)}</span>
                                    <span className="font-medium text-gray-800">{AUDIT_MODULE_LABELS[row.module_key] || row.module_key || 'General'}</span>
                                    <span className="text-xs text-gray-400">{row.http_method || 'GET'} · {row.endpoint_name || 'sin endpoint'}</span>
                                  </div>
                                  <div className="text-xs text-gray-500 break-all">{row.page_path || 'Sin ruta reportada'}</div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="mt-5 rounded-2xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-600">
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">Usuario</th>
                      <th className="px-4 py-3 font-medium">Evento</th>
                      <th className="px-4 py-3 font-medium">Módulo</th>
                      <th className="px-4 py-3 font-medium">Acción</th>
                      <th className="px-4 py-3 font-medium">Origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLoading && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">Cargando historial…</td>
                      </tr>
                    )}
                    {!auditLoading && auditRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No hay eventos de auditoría para los filtros seleccionados.</td>
                      </tr>
                    )}
                    {!auditLoading && auditRows.map((row) => (
                      <tr key={row.id} className="border-t border-gray-100 align-top">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{formatAuditDateTime(row.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{row.admin_user_name || 'Usuario'}</div>
                          <div className="text-xs text-gray-500">{row.admin_user_email || 'Sin correo'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold border ${row.event_type === 'login_success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-cyan-50 text-cyan-700 border-cyan-200'}`}>
                            {AUDIT_EVENT_LABELS[row.event_type] || row.event_type || 'Actividad'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{AUDIT_MODULE_LABELS[row.module_key] || row.module_key || 'General'}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{row.action_name || 'Sin acción'}</div>
                          <div className="text-xs text-gray-500">{row.http_method || 'GET'} · {row.endpoint_name || 'sin endpoint'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-700 break-all">{row.page_path || 'Sin ruta reportada'}</div>
                          <div className="text-xs text-gray-500 mt-1">{row.ip_address || 'Sin IP'}{row.details?.auth_mode ? ` · ${row.details.auth_mode}` : ''}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {auditHasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadMoreAuditRows}
                  disabled={auditLoadingMore}
                  className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
                >
                  {auditLoadingMore ? 'Cargando…' : `Cargar más (${auditRows.length} de ${auditMeta.total})`}
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {adminTiView === 'control-versiones-calidad' && (
        <div className="rounded-2xl p-[1px] shadow-lg bg-gradient-to-br from-emerald-100 via-white to-cyan-50">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Control versiones calidad</h3>
                <p className="mt-1 text-sm text-gray-600">Administra los imprimibles con nombre, código, versión y fecha para reflejarlos automáticamente en los formatos.</p>
              </div>
              <div className="text-xs text-gray-600 flex items-center gap-4">
                <div><span className="font-semibold">{qualityVersionTotal || qualityVersionRows.length || 0}</span> imprimibles</div>
                <div>Con versión: <span className="font-semibold">{qualityVersionRows.filter((r) => String(r?.version || '').trim() !== '').length}</span></div>
                <div>Con fecha: <span className="font-semibold">{qualityVersionRows.filter((r) => String(r?.effective_date || '').trim() !== '').length}</span></div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  {qualityVersionEditId ? 'Editar imprimible' : 'Nuevo imprimible'}
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del imprimible</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.document_name}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, document_name: e.target.value }))}
                        placeholder="Ej. Hoja de vida activos TI"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Clave interna</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.document_key}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, document_key: e.target.value }))}
                        placeholder="ej. hv_activos"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Código</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.code}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, code: e.target.value }))}
                        placeholder="FT-XXX-000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Versión</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.version}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, version: e.target.value }))}
                        placeholder="00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha</label>
                      <input
                        type="date"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.effective_date}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, effective_date: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Orden</label>
                      <input
                        type="number"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        value={qualityVersionForm.sort_order}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, sort_order: e.target.value }))}
                        min="0"
                        step="10"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 pt-6">
                      <input
                        type="checkbox"
                        checked={!!qualityVersionForm.active}
                        onChange={(e) => setQualityVersionForm((p) => ({ ...p, active: e.target.checked }))}
                      />
                      Imprimible activo
                    </label>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Descripción</label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 text-sm min-h-28"
                      value={qualityVersionForm.description}
                      onChange={(e) => setQualityVersionForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Describe el formato o su alcance"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={saveQualityVersion}
                      disabled={qualityVersionSaving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-white text-sm font-semibold"
                      style={{ backgroundColor: qualityVersionSaving ? '#34D399' : '#059669' }}
                    >
                      <Save size={16} />
                      {qualityVersionEditId ? (qualityVersionSaving ? 'Guardando...' : 'Guardar cambios') : (qualityVersionSaving ? 'Guardando...' : 'Agregar imprimible')}
                    </button>
                    {qualityVersionEditId ? (
                      <button
                        type="button"
                        onClick={resetQualityVersionForm}
                        className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
                      >
                        Cancelar
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">TOTAL</div>
                    <div className="text-2xl font-semibold">{qualityVersionRows.length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">ACTIVOS</div>
                    <div className="text-2xl font-semibold text-emerald-600">{qualityVersionRows.filter((r) => Number(r?.active ?? 1) !== 0).length}</div>
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="text-xs text-gray-500">CÓDIGOS DEFINIDOS</div>
                    <div className="text-2xl font-semibold text-cyan-700">{qualityVersionRows.filter((r) => String(r?.code || '').trim() !== '').length}</div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div className="text-sm text-gray-600">
                    Desde aquí cambias la versión y fecha y el imprimible la toma automáticamente al volver a generarlo.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="border rounded-full px-4 py-2 text-sm w-full md:w-72"
                      placeholder="Buscar por nombre, clave, código o versión..."
                      value={qualityVersionQuery}
                      onChange={(e) => setQualityVersionQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-4 border rounded-xl p-4 min-h-[340px] bg-gray-50">
                  {qualityVersionLoading ? (
                    <div className="text-sm text-gray-600">Cargando...</div>
                  ) : qualityVersionRows.length === 0 ? (
                    <div className="text-sm text-gray-500 text-center py-16">
                      <div className="font-semibold">No hay imprimibles configurados.</div>
                      <div className="mt-1">Agrega el primero desde el formulario.</div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {qualityVersionRows.map((r) => (
                        <div key={`qv-${r.id || r.document_key}`} className="bg-white border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-xs font-semibold text-gray-700">{r.document_name || '-'}</div>
                                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700">{r.document_key || '-'}</span>
                                {r.code ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-cyan-50 text-cyan-700 border-cyan-200">{r.code}</span> : null}
                                {r.version ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Versión {r.version}</span> : null}
                                {r.effective_date ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white text-gray-700">{r.effective_date}</span> : null}
                                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${Number(r?.active ?? 1) !== 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                  {Number(r?.active ?? 1) !== 0 ? 'Activo' : 'Inactivo'}
                                </span>
                              </div>
                              {r.description ? (
                                <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">{r.description}</div>
                              ) : null}
                              <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                                <div>Orden: <span className="font-semibold">{r.sort_order ?? 0}</span></div>
                                {r.updated_at ? <div>Actualizado: <span className="font-semibold">{r.updated_at}</span></div> : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="px-2 py-2 rounded-md border bg-white hover:bg-gray-50"
                                onClick={() => {
                                  setQualityVersionEditId(r.id || null);
                                  setQualityVersionForm({
                                    document_key: r.document_key || '',
                                    document_name: r.document_name || '',
                                    code: r.code || '',
                                    version: r.version || '',
                                    effective_date: r.effective_date || buildTodayYmd(),
                                    description: r.description || '',
                                    sort_order: r.sort_order ?? 0,
                                    active: Number(r?.active ?? 1) !== 0,
                                  });
                                }}
                                title="Editar"
                              >
                                <Pencil size={16} />
                              </button>
                              {r.id ? (
                                <button
                                  type="button"
                                  className="px-2 py-2 rounded-md border bg-white hover:bg-gray-50 text-red-600"
                                  onClick={() => deleteQualityVersion(r)}
                                  title="Eliminar"
                                >
                                  <Trash size={16} />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {adminTiView === 'hv-activos' && (
        <div className="rounded-2xl p-[1px] shadow-lg bg-gradient-to-br from-indigo-100 via-white to-orange-50">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">HV-ACTIVOS</h3>
                <p className="mt-1 text-sm text-gray-600">Hoja de vida de equipos (cómputo, monitores, periféricos).</p>
              </div>
              <button
                type="button"
                onClick={openCreateHv}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium transition-all duration-150 ease-out hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0 active:shadow-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
              >
                Nuevo activo
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                value={hvQuery}
                onChange={(e) => setHvQuery(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500"
                placeholder="Buscar por serial, marca, modelo, responsable..."
              />
              <select value={hvType} onChange={(e) => setHvType(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                <option value="Todos">Todos los tipos</option>
                <option value="Portátil">Portátil</option>
                <option value="PC">PC</option>
                <option value="Celular">Celulares</option>
                <option value="Tablet">Tablets</option>
                <option value="Servidor">Servidores</option>
                <option value="Monitor">Monitor</option>
                <option value="Impresora">Impresora</option>
                <option value="Periférico">Periférico</option>
                <option value="Otro">Otro</option>
              </select>
              <select value={hvStatus} onChange={(e) => setHvStatus(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                <option value="Todos">Todos los estados</option>
                <option value="Activo">Activo</option>
                <option value="En uso">En uso</option>
                <option value="En reparación">En reparación</option>
                <option value="Baja">Baja</option>
                <option value="Perdido">Perdido</option>
              </select>
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Tipo</th>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Serial</th>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Marca / Modelo</th>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Asignado a</th>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Estado</th>
                  <th className="text-left px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Sede</th>
                  <th className="text-right px-3 py-3 border-b text-[11px] font-semibold tracking-wide text-gray-700 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {hvLoading && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-600">Cargando...</td></tr>
                )}
                {!hvLoading && (!Array.isArray(hvRows) || hvRows.length === 0) && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-600">Sin activos registrados.</td></tr>
                )}
                {!hvLoading && Array.isArray(hvRows) && hvRows.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap">{r.asset_type}</td>
                    <td className="px-3 py-3 font-mono text-[12px] whitespace-nowrap">{r.serial}</td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900">{String(r.brand || '').trim() || '—'}</div>
                      <div className="text-xs text-gray-500">{String(r.model || '').trim() || '—'}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900">{r.assigned_to_name || r.assigned_admin_name || '—'}</div>
                      <div className="text-xs text-gray-500">{r.assigned_to_dni ? `DNI ${r.assigned_to_dni}` : '—'}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex flex-col items-start gap-1">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                          {r.sede_ubicacion || '—'}
                        </span>
                        <button type="button" onClick={() => openHvInspectionHistory(r.serial)} className="text-xs text-indigo-700 hover:underline">
                          Ver historial de inspecciones
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => printHvFormat(r)}
                          className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-indigo-200 bg-white text-indigo-700 text-xs font-medium transition-all duration-150 ease-out hover:bg-indigo-50 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0 active:shadow-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500"
                        >
                          Imprimir formato
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditHv(r)}
                          className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 text-xs font-medium transition-all duration-150 ease-out hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0 active:shadow-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteHv(r)}
                          className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-red-200 bg-white text-red-700 text-xs font-medium transition-all duration-150 ease-out hover:bg-red-50 hover:-translate-y-0.5 hover:shadow-sm active:translate-y-0 active:shadow-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
        )}

        {adminTiView === 'soportes' && (
        <div className="overflow-x-auto">
          <div className="grid gap-6 items-start" style={{ minWidth: 1020, gridTemplateColumns: '45% 55%' }}>
          <div style={{ width: '100%' }}>
            <div className="w-full rounded-2xl p-4 border border-orange-100" style={{ backgroundColor: '#FFF7ED' }}>
              <div className="rounded-2xl p-[1px] shadow-lg" style={{ background: 'linear-gradient(135deg, rgba(240,126,45,0.35) 0%, rgba(245,158,11,0.22) 45%, rgba(255,255,255,0) 100%)' }}>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
            <div className="border-r border-gray-200 bg-gray-50">
              <div className="px-4 py-4 border-b border-gray-200" style={{ background: 'linear-gradient(90deg, #F07E2D 0%, #F59E0B 100%)' }}>
                <div className="text-xs font-semibold tracking-wide text-white">MESA DE AYUDA</div>
                <div className="text-[11px] text-white/90 mt-1">Gestión y seguimiento de tickets</div>
              </div>
              <div className="p-3 flex flex-col gap-1 text-sm">
                <button
                  type="button"
                  onClick={() => setSupportsStatus('Todos')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border ${supportsStatus === 'Todos' ? 'bg-white font-semibold shadow-sm' : 'bg-transparent border-transparent hover:bg-white/60 hover:border-gray-200'}`}
                  style={supportsStatus === 'Todos' ? { borderColor: '#F07E2D' } : undefined}
                >
                  <span>Todos los tickets</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSupportsStatus('Abierto')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border ${supportsStatus === 'Abierto' ? 'bg-white font-semibold shadow-sm' : 'bg-transparent border-transparent hover:bg-white/60 hover:border-gray-200'}`}
                  style={supportsStatus === 'Abierto' ? { borderColor: '#F07E2D' } : undefined}
                >
                  <span>Abiertos</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">{supportsCounts.abiertos}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSupportsStatus('En Proceso')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border ${supportsStatus === 'En Proceso' ? 'bg-white font-semibold shadow-sm' : 'bg-transparent border-transparent hover:bg-white/60 hover:border-gray-200'}`}
                  style={supportsStatus === 'En Proceso' ? { borderColor: '#F07E2D' } : undefined}
                >
                  <span>En progreso</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">{supportsCounts.enProceso}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSupportsStatus('Cerrado')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border ${supportsStatus === 'Cerrado' ? 'bg-white font-semibold shadow-sm' : 'bg-transparent border-transparent hover:bg-white/60 hover:border-gray-200'}`}
                  style={supportsStatus === 'Cerrado' ? { borderColor: '#F07E2D' } : undefined}
                >
                  <span>Cerrados</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">{supportsCounts.cerrados}</span>
                </button>
              </div>
            </div>

            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {supportsStatus === 'Todos' ? 'Todos los tickets' : (supportsStatus === 'Abierto' ? 'Abiertos' : (supportsStatus === 'En Proceso' ? 'En progreso' : 'Cerrados'))}
                </h3>
                <button
                  type="button"
                  onClick={() => { setSupportsForm({ subject: '', description: '', category: 'General', priority: 'Media', requester_name: '', requester_dni: '', requester_email: '', assigned_to_admin_id: '' }); setSupportsCreateOpen(true); }}
                  className="px-3 py-2 rounded-lg text-sm text-white shadow-sm hover:opacity-95"
                  style={{ backgroundColor: '#F07E2D' }}
                >
                  + Nuevo ticket
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="min-w-0">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                      <div className="text-xs text-gray-600">Total</div>
                      <div className="text-2xl font-semibold" style={{ color: '#111827' }}>{supportsCounts.total}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                      <div className="text-xs text-gray-600">Abiertos</div>
                      <div className="text-2xl font-semibold text-blue-700">{supportsCounts.abiertos}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                      <div className="text-xs text-gray-600">Cerrados</div>
                      <div className="text-2xl font-semibold text-green-700">{supportsCounts.cerrados}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSupportsPriority('Todos')}
                      className={`px-3 py-1 text-sm rounded-full border ${supportsPriority === 'Todos' ? 'font-semibold' : 'border-gray-300 hover:bg-gray-50'}`}
                      style={supportsPriority === 'Todos' ? { backgroundColor: '#FFF7ED', borderColor: '#F07E2D', color: '#9A3412' } : undefined}
                    >
                      Todas las prioridades
                    </button>
                    {['Alta', 'Media', 'Baja'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setSupportsPriority(p)}
                        className={`px-3 py-1 text-sm rounded-full border ${supportsPriority === p ? 'font-semibold' : 'border-gray-300 hover:bg-gray-50'}`}
                        style={supportsPriority === p ? { backgroundColor: '#FFF7ED', borderColor: '#F07E2D', color: '#9A3412' } : undefined}
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <div className="mt-4">
                    {supportsLoading && (
                      <div className="text-sm text-gray-500">Cargando…</div>
                    )}
                    {!supportsLoading && supportsFilteredRows.length === 0 && (
                      <div className="text-sm text-gray-500">No hay tickets.</div>
                    )}
                    {!supportsLoading && supportsFilteredRows.map((r) => {
                  const requesterName = String(r?.requester_name || '').trim() || '—';
                  const dept = (() => {
                    const d1 = String(r?.department || '').trim();
                    if (d1) return d1;
                    const desc = String(r?.description || '');
                    const m = desc.match(/Área\/Departamento:\s*(.*)/i);
                    if (!m) return '';
                    return String(m[1] || '').split('\n')[0].trim();
                  })();
                  const dt = String(r?.created_at || '').slice(0, 10);
                  const category = String(r?.category || 'General').trim() || 'General';
                  const leftBar = (() => {
                    const s = String(r?.status || '').trim();
                    if (s === 'Abierto') return '#2563EB';
                    if (s === 'En Proceso') return '#F59E0B';
                    if (s === 'Cerrado') return '#16A34A';
                    return '#9CA3AF';
                  })();
                  const daysLabel = (() => {
                    const aid = r?.assigned_to_admin_id ?? null;
                    if (!aid) return 'Pendiente asignar';
                    const src = r?.assigned_at || r?.updated_at || r?.created_at;
                    const d = new Date(String(src || '').replace(' ', 'T'));
                    if (Number.isNaN(d.getTime())) return 'Asignado';
                    const diff = Math.max(0, Math.ceil((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
                    return `Asignado hace ${diff} d`;
                  })();
                  return (
                    <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-3 mb-3 shadow-sm hover:shadow-md transition-shadow" style={{ borderLeftWidth: 6, borderLeftColor: leftBar }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-semibold text-gray-500">{formatTicketCode(r.id)}</span>
                            <span className="font-semibold truncate">{r.subject}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded-full border ${statusPillClass(r.status)}`}>{r.status}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full border ${priorityPillClass(r.priority)}`}>{r.priority}</span>
                            <span className="px-2 py-0.5 text-xs rounded-full border bg-gray-50 text-gray-700 border-gray-200">{category}</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-600 flex flex-wrap items-center gap-2">
                            <span className="truncate">Solicitante: {requesterName}</span>
                            <span className="text-gray-300">•</span>
                            <span className="truncate">Área: {dept || '—'}</span>
                            <span className="text-gray-300">•</span>
                            <span className="truncate">Categoría: {category}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button type="button" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm" onClick={() => openEditSupport(r)}>
                            <Pencil size={16} /> Editar
                          </button>
                          <button type="button" className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm" onClick={() => deleteSupport(r.id)}>
                            <Trash size={16} /> Eliminar
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600 flex items-center justify-end gap-2">
                        <span className="truncate">{daysLabel}</span>
                        <span className="text-gray-300">•</span>
                        <span>{dt}</span>
                      </div>
                    </div>
                  );
                    })}
                  </div>
                </div>
              </div>
              </div>
            </div>
              </div>
            </div>
          </div>
          </div>

          <div className="w-full" style={{ backgroundColor: '#FBFAF7' }}>
            <div className="rounded-2xl border border-gray-200 bg-[#FBFAF7] p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setDashPreset('week');
                    const now = new Date();
                    const day = (now.getDay() + 6) % 7;
                    const start = new Date(now);
                    start.setDate(now.getDate() - day);
                    setDashFrom(start.toISOString().slice(0, 10));
                    setDashTo(now.toISOString().slice(0, 10));
                  }}
                  className={`px-3 py-1 text-xs rounded-md border ${dashPreset === 'week' ? 'bg-white font-semibold border-gray-300' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                >
                  Esta semana
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDashPreset('month');
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), 1);
                    setDashFrom(start.toISOString().slice(0, 10));
                    setDashTo(now.toISOString().slice(0, 10));
                  }}
                  className={`px-3 py-1 text-xs rounded-md border ${dashPreset === 'month' ? 'bg-white font-semibold border-gray-300' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                >
                  Este mes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDashPreset('quarter');
                    const now = new Date();
                    const q = Math.floor(now.getMonth() / 3) * 3;
                    const start = new Date(now.getFullYear(), q, 1);
                    setDashFrom(start.toISOString().slice(0, 10));
                    setDashTo(now.toISOString().slice(0, 10));
                  }}
                  className={`px-3 py-1 text-xs rounded-md border ${dashPreset === 'quarter' ? 'bg-white font-semibold border-gray-300' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                >
                  Trimestre
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <input type="date" value={dashFrom} onChange={(e)=>{ setDashPreset('custom'); setDashFrom(e.target.value); }} className="border border-gray-200 bg-white rounded px-2 py-1 text-xs" />
                  <span className="text-xs text-gray-500">a</span>
                  <input type="date" value={dashTo} onChange={(e)=>{ setDashPreset('custom'); setDashTo(e.target.value); }} className="border border-gray-200 bg-white rounded px-2 py-1 text-xs" />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-gray-200 p-3 bg-[#F3F0E8]">
                  <div className="text-[11px] text-gray-600">Total generados</div>
                  <div className="text-2xl font-semibold text-gray-900">{dashboardData.totalGenerated}</div>
                  <div className="text-[11px] text-gray-600">{dashboardData.pctVsPrev === null ? '— vs periodo ant.' : `${dashboardData.pctVsPrev >= 0 ? '+' : ''}${dashboardData.pctVsPrev}% vs periodo ant.`}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3 bg-[#F3F0E8]">
                  <div className="text-[11px] text-gray-600">Pendientes</div>
                  <div className="text-2xl font-semibold text-gray-900">{dashboardData.pending}</div>
                  <div className="text-[11px] text-gray-600">{dashboardData.unassigned} sin asignar</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3 bg-[#F3F0E8]">
                  <div className="text-[11px] text-gray-600">En progreso</div>
                  <div className="text-2xl font-semibold text-gray-900">{dashboardData.inProcess}</div>
                  <div className="text-[11px] text-gray-600">{dashboardData.unassignedInProcess} sin asignar</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3 bg-[#F3F0E8]">
                  <div className="text-[11px] text-gray-600">Cerrados</div>
                  <div className="text-2xl font-semibold text-gray-900">{dashboardData.closed}</div>
                  <div className="text-[11px] text-gray-600">Tasa: {dashboardData.closedRate}%</div>
                </div>
              </div>

              <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Tickets generados vs cerrados</div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-600">
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-blue-600" /> Generados</span>
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-green-600" /> Cerrados</span>
                    <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-orange-500" /> Pendientes</span>
                  </div>
                </div>
                <div
                  className="mt-3 h-44 flex items-end gap-2 rounded-lg p-2"
                  style={{ backgroundImage: 'linear-gradient(#F3F4F6 1px, transparent 1px)', backgroundSize: '100% 22px' }}
                >
                  {dashboardData.series.map((d) => (
                    <div key={d.label} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full h-32 flex items-end justify-center gap-1">
                        <div className="w-3 rounded-t bg-blue-600" style={{ height: `${Math.round((d.generated / dashboardData.maxSeries) * 100)}%` }} />
                        <div className="w-3 rounded-t bg-green-600" style={{ height: `${Math.round((d.closed / dashboardData.maxSeries) * 100)}%` }} />
                        <div className="w-3 rounded-t bg-orange-500" style={{ height: `${Math.round((d.pending / dashboardData.maxSeries) * 100)}%` }} />
                      </div>
                      <div className="text-[10px] text-gray-600">{d.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="text-sm font-semibold mb-3">Por categoría</div>
                  <div className="space-y-2">
                    {dashboardData.byCategory.arr.slice(0, 6).map((it, idx) => {
                      const c = ['#3B82F6', '#16A34A', '#F59E0B', '#8B5CF6', '#EF4444', '#0EA5E9'][idx % 6];
                      return (
                        <div key={it.cat} className="flex items-center gap-2">
                          <div className="w-28 text-[11px] text-gray-700 truncate">{it.cat}</div>
                          <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(2, Math.round((it.count / dashboardData.byCategory.max) * 100))}%`, backgroundColor: c }} />
                          </div>
                          <div className="w-7 text-right text-[11px] font-semibold text-gray-800">{it.count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="text-sm font-semibold mb-3">Por prioridad</div>
                  <div className="flex items-center gap-4">
                    <div
                      className="relative w-28 h-28 rounded-full"
                      style={{
                        background: `conic-gradient(#EF4444 0 ${dashboardData.byPriority.pctAlta}%, #F59E0B ${dashboardData.byPriority.pctAlta}% ${dashboardData.byPriority.pctAlta + dashboardData.byPriority.pctMedia}%, #16A34A ${dashboardData.byPriority.pctAlta + dashboardData.byPriority.pctMedia}% 100%)`,
                      }}
                    >
                      <div className="absolute inset-3 bg-white rounded-full border border-gray-200" />
                    </div>
                    <div className="text-[11px] text-gray-700 space-y-1">
                      <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded bg-red-500" /> Alta <span className="ml-auto font-semibold">{dashboardData.byPriority.Alta}</span></div>
                      <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded bg-orange-500" /> Media <span className="ml-auto font-semibold">{dashboardData.byPriority.Media}</span></div>
                      <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded bg-green-600" /> Baja <span className="ml-auto font-semibold">{dashboardData.byPriority.Baja}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-gray-600">Tiempo promedio de cierre</div>
                  <div className="text-sm font-semibold text-gray-900">{dashboardData.avgCloseDays.n ? `${dashboardData.avgCloseDays.days} días` : '—'}</div>
                </div>
                <div className="text-[11px] text-gray-600">{dashboardData.avgCloseDays.n ? `${dashboardData.avgCloseDays.n} cierres en rango` : 'Sin cierres en rango'}</div>
              </div>

              <div className="mt-3 bg-white border border-gray-200 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Cierres por responsable asignado</div>
                  <div className="text-[11px] text-gray-600">{dashboardData.byAssigneeClosed.arr.length ? `${dashboardData.byAssigneeClosed.arr.length} personas` : 'Sin datos'}</div>
                </div>
                <div className="mt-3 space-y-2">
                  {dashboardData.byAssigneeClosed.arr.length ? dashboardData.byAssigneeClosed.arr.slice(0, 8).map((it) => (
                    <div key={it.name} className="flex items-center gap-2">
                      <div className="w-44 text-[11px] text-gray-700 truncate">{it.name}</div>
                      <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(2, Math.round((it.count / dashboardData.byAssigneeClosed.max) * 100))}%`,
                            backgroundImage: 'linear-gradient(90deg, #60A5FA 0%, #34D399 55%, #F59E0B 100%)',
                          }}
                        />
                      </div>
                      <div className="w-7 text-right text-[11px] font-semibold text-gray-800">{it.count}</div>
                    </div>
                  )) : (
                    <div className="text-[11px] text-gray-600">No hay cierres en el rango seleccionado con responsable asignado.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        )}

        {/* Vista de perfil de usuario removida; permisos ahora se editan inline por fila */}
      </div>

      <Modal
        isOpen={supportsCreateOpen}
        title="Nuevo ticket"
        onClose={() => setSupportsCreateOpen(false)}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={supportsForm.subject}
              onChange={(e) => setSupportsForm((p) => ({ ...p, subject: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Asunto"
            />
            <select
              value={supportsForm.priority}
              onChange={(e) => setSupportsForm((p) => ({ ...p, priority: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </select>
            <input
              type="text"
              value={supportsForm.category}
              onChange={(e) => setSupportsForm((p) => ({ ...p, category: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Categoría (Ej: Hardware)"
            />
            <select
              value={supportsForm.assigned_to_admin_id}
              onChange={(e) => setSupportsForm((p) => ({ ...p, assigned_to_admin_id: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">Sin asignar</option>
              {(Array.isArray(users) ? users : []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              value={supportsForm.requester_dni}
              onChange={(e) => setSupportsForm((p) => ({ ...p, requester_dni: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Cédula solicitante"
            />
            <input
              type="text"
              value={supportsForm.requester_name}
              onChange={(e) => setSupportsForm((p) => ({ ...p, requester_name: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Nombre solicitante"
            />
            <input
              type="email"
              value={supportsForm.requester_email}
              onChange={(e) => setSupportsForm((p) => ({ ...p, requester_email: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Correo solicitante"
            />
          </div>
          <textarea
            value={supportsForm.description}
            onChange={(e) => setSupportsForm((p) => ({ ...p, description: e.target.value }))}
            className="border rounded px-3 py-2 text-sm w-full"
            rows={4}
            placeholder="Descripción"
          />
          <div className="flex items-center gap-2 justify-end">
            <button type="button" className="px-3 py-2 rounded border" onClick={() => setSupportsCreateOpen(false)}>
              Cancelar
            </button>
            <button type="button" className="px-3 py-2 rounded bg-green-600 text-white" onClick={createSupport}>
              Crear
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={supportsEditOpen}
        title="Editar ticket"
        onClose={() => { setSupportsEditOpen(false); setSupportsEditing(null); }}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={supportsEditing?.subject || ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), subject: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Asunto"
            />
            <select
              value={supportsEditing?.status || 'Abierto'}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), status: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="Abierto">Abierto</option>
              <option value="En Proceso">En Proceso</option>
              <option value="Cerrado">Cerrado</option>
            </select>
            <select
              value={supportsEditing?.priority || 'Media'}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), priority: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="Baja">Baja</option>
              <option value="Media">Media</option>
              <option value="Alta">Alta</option>
            </select>
            <input
              type="text"
              value={supportsEditing?.category || ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), category: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Categoría"
            />
            <select
              value={supportsEditing?.assigned_to_admin_id ?? ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), assigned_to_admin_id: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">Sin asignar</option>
              {(Array.isArray(users) ? users : []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              value={supportsEditing?.requester_dni || ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), requester_dni: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Cédula solicitante"
            />
            <input
              type="text"
              value={supportsEditing?.requester_name || ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), requester_name: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Nombre solicitante"
            />
            <input
              type="email"
              value={supportsEditing?.requester_email || ''}
              onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), requester_email: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Correo solicitante"
            />
          </div>
          <textarea
            value={supportsEditing?.description || ''}
            onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), description: e.target.value }))}
            className="border rounded px-3 py-2 text-sm w-full"
            rows={4}
            placeholder="Descripción"
          />
          <textarea
            value={supportsEditing?.resolution || ''}
            onChange={(e) => setSupportsEditing((p) => ({ ...(p || {}), resolution: e.target.value }))}
            className="border rounded px-3 py-2 text-sm w-full"
            rows={3}
            placeholder="Solución / cierre"
          />
          <div className="flex items-center gap-2 justify-end">
            <button type="button" className="px-3 py-2 rounded border" onClick={() => { setSupportsEditOpen(false); setSupportsEditing(null); }}>
              Cancelar
            </button>
            <button type="button" className="px-3 py-2 rounded bg-green-600 text-white" onClick={saveEditSupport}>
              Guardar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={hvCreateOpen}
        title="Formato HV-ACTIVOS"
        onClose={() => { setHvCreateOpen(false); setHvEditing(null); }}
      >
        <div className="space-y-4">
          <datalist id="hv-people-dni">
            {hvPeopleOptions.map((p) => (
              <option key={p.dni} value={p.dni}>{p.name}</option>
            ))}
          </datalist>
          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-gray-700">1. EQUIPOS</div>
              <button type="button" onClick={hvCreateAddEquipment} className="px-3 py-2 rounded border text-sm">+ Agregar equipo</button>
            </div>
            {(Array.isArray(hvEditing?.equipments) ? hvEditing.equipments : []).map((eq, idx) => (
              <div key={`eq-${idx}`} className="border rounded-lg p-3 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-gray-700">EQUIPO #{idx + 1}</div>
                  <button type="button" onClick={() => hvCreateRemoveEquipment(idx)} className="px-2 py-1 rounded border text-xs">Eliminar</button>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <select
                    value={eq.material_code || ''}
                    onChange={(e) => {
                      const code = e.target.value;
                      const mat = materialByCode.get(String(code));
                      const inferredType = mat ? inferAssetTypeFromMaterial(mat?.name, mat?.code) : 'Periférico';
                      hvCreateUpdateEquipment(idx, {
                        material_code: code,
                        asset_type: inferredType,
                        brand: '',
                        serial: '',
                      });
                    }}
                    className="border rounded px-3 py-2 text-sm"
                  >
                    <option value="">Seleccione material (Tecnología)</option>
                    {techMaterials.map((m) => (
                      <option key={m.id} value={m.code}>
                        {(m.code ? `${m.code} - ` : '') + (m.name || 'Material')}
                      </option>
                    ))}
                  </select>
                  <select
                    value={eq._brandItemKey ?? ''}
                    onChange={(e) => {
                      const key = e.target.value;
                      if (!key) { hvCreateUpdateEquipment(idx, { _brandItemKey: '' }); return; }
                      const [code, serial] = String(key).split('||');
                      const list = brandItemsByMaterialCode.get(String(code)) || [];
                      const it = list.find((x) => String(x?.serial || '').trim() === String(serial || '').trim());
                      if (!it) return;
                      hvCreateUpdateEquipment(idx, {
                        _brandItemKey: key,
                        brand: String(it?.name || it?.brand || '').trim(),
                        serial: String(it?.serial || '').trim(),
                        material_code: String(code),
                      });
                    }}
                    className="border rounded px-3 py-2 text-sm"
                    disabled={!String(eq.material_code || '').trim()}
                  >
                    <option value="">{String(eq.material_code || '').trim() ? 'Seleccione serial / marca' : 'Seleccione un material primero'}</option>
                    {(brandItemsByMaterialCode.get(String(eq.material_code || '').trim()) || []).map((bi) => (
                      <option key={`${String(eq.material_code || '').trim()}||${String(bi.serial || '').trim()}`} value={`${String(eq.material_code || '').trim()}||${String(bi.serial || '').trim()}`}>
                        {(String(bi?.name || bi?.brand || '').trim() ? `${String(bi?.name || bi?.brand || '').trim()} | ` : '') + String(bi.serial || '').trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <select value={eq.asset_type || 'Portátil'} onChange={(e) => hvCreateUpdateEquipment(idx, { asset_type: e.target.value })} className="border rounded px-3 py-2 text-sm">
                    <option value="Portátil">Portátil</option>
                    <option value="PC">PC</option>
                  <option value="Celular">Celulares</option>
                  <option value="Tablet">Tablets</option>
                  <option value="Servidor">Servidores</option>
                    <option value="Monitor">Monitor</option>
                    <option value="Impresora">Impresora</option>
                    <option value="Periférico">Periférico</option>
                    <option value="Otro">Otro</option>
                  </select>
                  <input type="text" value={eq.serial || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { serial: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="N° de serie" />
                  <select value={eq.status || 'Activo'} onChange={(e) => hvCreateUpdateEquipment(idx, { status: e.target.value, estado_equipo: e.target.value })} className="border rounded px-3 py-2 text-sm">
                    <option value="Activo">Activo</option>
                    <option value="En uso">En uso</option>
                    <option value="En reparación">En reparación</option>
                    <option value="Baja">Baja</option>
                    <option value="Perdido">Perdido</option>
                  </select>
                  <select value={eq.sede_ubicacion || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { sede_ubicacion: e.target.value })} className="border rounded px-3 py-2 text-sm">
                    <option value="">Sede</option>
                    <option value="Popayán">Popayán</option>
                    <option value="Santander de Quilichao">Santander de Quilichao</option>
                    <option value="Cali">Cali</option>
                  </select>
                </div>
                <div className="flex items-center justify-end">
                  <button type="button" onClick={() => hvCreateUpdateEquipment(idx, { _showMore: !eq._showMore })} className="px-2 py-1 rounded border text-xs">
                    {eq._showMore ? 'Ocultar detalles' : 'Ver detalles'}
                  </button>
                </div>
                {eq._showMore && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <input type="text" value={eq.brand || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { brand: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="Marca" />
                    <input type="text" value={eq.model || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { model: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="Modelo" />
                    <input type="text" value={eq.sistema_operativo || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { sistema_operativo: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="Sistema operativo" />
                    <input type="text" value={eq.accesorios || ''} onChange={(e) => hvCreateUpdateEquipment(idx, { accesorios: e.target.value })} className="border rounded px-3 py-2 text-sm sm:col-span-2 lg:col-span-3" placeholder="Accesorios incluidos" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-gray-700">2. LICENCIAS DE SOFTWARE</div>
              <button type="button" onClick={hvCreateAddLicense} className="px-3 py-2 rounded border text-sm">+ Agregar licencia</button>
            </div>
            {(Array.isArray(hvEditing?.software_licenses) ? hvEditing.software_licenses : [{ name: '', license: '', expiry: '', notes: '' }]).map((lic, i) => (
              <div key={`lic-${i}`} className="border rounded-lg p-3 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-gray-700">LICENCIA #{i + 1}</div>
                  <button type="button" onClick={() => hvCreateRemoveLicense(i)} className="px-2 py-1 rounded border text-xs">Eliminar</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <input type="text" value={lic.name || ''} onChange={(e) => hvCreateUpdateLicense(i, { name: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="Nombre del software" />
                  <input type="text" value={lic.license || ''} onChange={(e) => hvCreateUpdateLicense(i, { license: e.target.value })} className="border rounded px-3 py-2 text-sm" placeholder="N° licencia / clave" />
                  <input type="date" value={lic.expiry || ''} onChange={(e) => hvCreateUpdateLicense(i, { expiry: e.target.value })} className="border rounded px-3 py-2 text-sm" />
                </div>
                <textarea value={lic.notes || ''} onChange={(e) => hvCreateUpdateLicense(i, { notes: e.target.value })} className="border rounded px-3 py-2 text-sm w-full" rows={2} placeholder="Observaciones de software" />
              </div>
            ))}
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-gray-700">3. INSPECCIÓN DE EQUIPOS</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="date" value={hvEditing?.inspection_date || ''} onChange={(e) => setHvEditing((p) => ({ ...(p || {}), inspection_date: e.target.value }))} className="border rounded px-3 py-2 text-sm" />
              <select value={hvEditing?.inspection_type || ''} onChange={(e) => setHvEditing((p) => ({ ...(p || {}), inspection_type: e.target.value }))} className="border rounded px-3 py-2 text-sm">
                <option value="">Tipo de inspección</option>
                <option value="Alistamiento equipo">Alistamiento equipo</option>
                <option value="Limpieza">Limpieza</option>
                <option value="Cambio">Cambio</option>
                <option value="Traslado">Traslado</option>
                <option value="Disposición final">Disposición final</option>
              </select>
            </div>
            <textarea value={hvEditing?.inspection_observation || ''} onChange={(e) => setHvEditing((p) => ({ ...(p || {}), inspection_observation: e.target.value }))} className="border rounded px-3 py-2 text-sm w-full" rows={3} placeholder="Observación de la inspección" />
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-gray-700">4. ASIGNACIÓN DE RESPONSABLE (OPCIONAL)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                type="text"
                value={hvEditing?.assigned_to_dni || ''}
                onChange={(e) => handleHvFuncionarioDocChange(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
                placeholder="N° documento / cédula"
                list="hv-people-dni"
              />
              <input
                type="text"
                value={(() => {
                  const p = findPersonByDoc(hvEditing?.assigned_to_dni || '');
                  return p ? String(p.name || p.nombre || p.nombre_completo || '') : '';
                })()}
                className="border rounded px-3 py-2 text-sm bg-gray-100"
                placeholder="Apellidos y nombres"
                readOnly
              />
              <input
                type="text"
                value={(() => {
                  const p = findPersonByDoc(hvEditing?.assigned_to_dni || '');
                  return p ? String(p.position || p.role || '') : '';
                })()}
                className="border rounded px-3 py-2 text-sm bg-gray-100"
                placeholder="Cargo"
                readOnly
              />
              <input
                type="text"
                value={(() => {
                  const p = findPersonByDoc(hvEditing?.assigned_to_dni || '');
                  return p ? String(p.area || '') : '';
                })()}
                className="border rounded px-3 py-2 text-sm bg-gray-100"
                placeholder="Área"
                readOnly
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="date"
                value={hvEditing?.assignment_date || ''}
                onChange={(e) => setHvEditing((p) => ({ ...(p || {}), assignment_date: e.target.value }))}
                className="border rounded px-3 py-2 text-sm"
              />
              <div className="hidden sm:block" />
            </div>
            <textarea
              value={hvEditing?.assignment_reason || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), assignment_reason: e.target.value }))}
              className="border rounded px-3 py-2 text-sm w-full"
              rows={3}
              placeholder="Motivo / justificación"
            />

            {Array.isArray(hvEditing?.equipments) && hvEditing.equipments.some((e) => String(e?.serial || '').trim()) && (
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="text-xs font-semibold text-gray-700">Historial de responsables por serial</div>
                <div className="mt-2 space-y-2">
                  {hvEditing.equipments
                    .map((e) => String(e?.serial || '').trim())
                    .filter((s, i, arr) => s && arr.indexOf(s) === i)
                    .map((serial) => (
                      <div key={serial} className="border rounded p-2 bg-white">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-gray-800">Serial: {serial}</div>
                          <button type="button" onClick={() => loadHvResponsibleHistory(serial)} className="px-2 py-1 rounded border text-xs">Actualizar</button>
                        </div>
                        {hvResponsibleHistory[serial] && (
                          <div className="mt-2 overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2 border-b">De</th>
                                  <th className="text-left p-2 border-b">A</th>
                                  <th className="text-left p-2 border-b">Motivo</th>
                                  <th className="text-left p-2 border-b">Fecha</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(hvResponsibleHistory[serial] || []).length ? (hvResponsibleHistory[serial] || []).map((r) => (
                                  <tr key={r.id} className="border-b">
                                    <td className="p-2">{[r.from_name, r.from_dni].filter(Boolean).join(' - ') || '—'}</td>
                                    <td className="p-2">{[r.to_name, r.to_dni].filter(Boolean).join(' - ') || '—'}</td>
                                    <td className="p-2">{String(r.assignment_reason || '').trim() || '—'}</td>
                                    <td className="p-2">{String(r.assignment_date || '').trim() || String(r.changed_at || '').replace('T',' ').slice(0, 19) || '—'}</td>
                                  </tr>
                                )) : (
                                  <tr><td className="p-2 text-gray-600" colSpan={4}>Sin historial.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-gray-700">5. OBSERVACIONES GENERALES</div>
            <textarea value={hvEditing?.general_notes || ''} onChange={(e) => setHvEditing((p) => ({ ...(p || {}), general_notes: e.target.value }))} className="border rounded px-3 py-2 text-sm w-full" rows={3} placeholder="Condiciones especiales, daños preexistentes, compromisos..." />
          </div>

          <div className="rounded-lg border bg-white p-4 space-y-3">
            <div className="text-xs font-semibold text-gray-700">6. PROTOCOLO DE DIAGNÓSTICO</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 border-b">Categoría</th>
                    <th className="text-left p-2 border-b">Punto de verificación</th>
                    <th className="text-center p-2 border-b">Bueno</th>
                    <th className="text-center p-2 border-b">Regular</th>
                    <th className="text-center p-2 border-b">Malo</th>
                    <th className="text-center p-2 border-b">N/A</th>
                  </tr>
                </thead>
                <tbody>
                  {HV_DIAG_PROTOCOL.flatMap((cat) => {
                    const rows = cat.items.map((it, i) => {
                      const val = (hvEditing?.diagnostic_protocol || {})[it.id] || '';
                      const setVal = (next) => setHvEditing((p) => ({ ...(p || {}), diagnostic_protocol: { ...((p || {}).diagnostic_protocol || {}), [it.id]: next } }));
                      return (
                        <tr key={it.id} className="border-b">
                          {i === 0 && (
                            <td className="p-2 align-top border-r font-semibold" rowSpan={cat.items.length}>
                              {cat.category}
                            </td>
                          )}
                          <td className="p-2">{it.label}</td>
                          {['Bueno', 'Regular', 'Malo', 'N/A'].map((opt) => (
                            <td key={opt} className="p-2 text-center">
                              <input
                                type="radio"
                                name={`diag_${it.id}`}
                                checked={val === opt}
                                onChange={() => setVal(opt)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    });
                    return rows;
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="px-3 py-2 rounded border" onClick={() => { setHvCreateOpen(false); setHvEditing(null); }}>
              Cancelar
            </button>
            <button type="button" className="px-3 py-2 rounded border border-indigo-200 text-indigo-700 bg-white disabled:opacity-50" onClick={saveHvInspectionBackup} disabled={hvInspectionSaving}>
              {hvInspectionSaving ? 'Guardando inspección...' : 'Guardar inspección'}
            </button>
            <button type="button" className="px-3 py-2 rounded bg-green-600 text-white disabled:opacity-50" onClick={saveHv} disabled={hvSaving}>
              {hvSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={hvInspectionModalOpen}
        title={`Historial de inspecciones - ${hvInspectionSerial}`}
        onClose={() => { setHvInspectionModalOpen(false); setHvInspectionSerial(''); setHvInspectionFiles([]); }}
      >
        <div className="space-y-3">
          {hvInspectionLoading ? (
            <div className="text-sm text-gray-600">Cargando...</div>
          ) : (Array.isArray(hvInspectionFiles) && hvInspectionFiles.length) ? (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 border-b">Archivo</th>
                    <th className="text-left p-2 border-b">Fecha</th>
                    <th className="text-right p-2 border-b">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {hvInspectionFiles.map((f) => (
                    <tr key={f.id} className="border-b">
                      <td className="p-2">{f.filename}</td>
                      <td className="p-2">{String(f.created_at || '').replace('T',' ').slice(0, 19) || '—'}</td>
                      <td className="p-2 text-right">
                        {f.file_exists === false ? (
                          <span className="text-xs text-red-600">Archivo no disponible</span>
                        ) : (
                          <a href={f.download_url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">
                            Ver PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-gray-600">Sin inspecciones guardadas.</div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={hvEditOpen}
        title="Editar activo"
        onClose={() => { setHvEditOpen(false); setHvEditing(null); }}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={hvEditing?.asset_type || 'Portátil'}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), asset_type: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="Portátil">Portátil</option>
              <option value="PC">PC</option>
              <option value="Celular">Celulares</option>
              <option value="Tablet">Tablets</option>
              <option value="Servidor">Servidores</option>
              <option value="Monitor">Monitor</option>
              <option value="Impresora">Impresora</option>
              <option value="Periférico">Periférico</option>
              <option value="Otro">Otro</option>
            </select>
            <input
              type="text"
              value={hvEditing?.serial || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), serial: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Serial (obligatorio)"
            />
            <input
              type="text"
              value={hvEditing?.brand || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), brand: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Marca"
            />
            <input
              type="text"
              value={hvEditing?.model || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), model: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Modelo"
            />
            <input
              type="text"
              value={hvEditing?.hostname || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), hostname: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Hostname"
            />
            <select
              value={hvEditing?.status || 'Activo'}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), status: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="Activo">Activo</option>
              <option value="En uso">En uso</option>
              <option value="En reparación">En reparación</option>
              <option value="Baja">Baja</option>
              <option value="Perdido">Perdido</option>
            </select>
            <input
              type="text"
              value={hvEditing?.asset_tag || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), asset_tag: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Etiqueta / Placa"
            />
            <input
              type="text"
              value={hvEditing?.material_code || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), material_code: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Código de material (opcional)"
            />
            <input
              type="date"
              value={hvEditing?.purchase_date || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), purchase_date: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={hvEditing?.warranty_until || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), warranty_until: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={hvEditing?.location || ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), location: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
              placeholder="Ubicación"
            />
            <select
              value={hvEditing?.assigned_to_dni || ''}
              onChange={(e) => {
                const dni = e.target.value;
                const found = hvPeopleOptions.find((x) => x.dni === dni);
                setHvEditing((p) => ({ ...(p || {}), assigned_to_dni: dni, assigned_to_name: found ? found.name : '' }));
              }}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">Asignado a (persona)</option>
              {hvPeopleOptions.map((p) => (
                <option key={p.dni} value={p.dni}>{p.name} - {p.dni}</option>
              ))}
            </select>
            <select
              value={hvEditing?.assigned_to_admin_id ?? ''}
              onChange={(e) => setHvEditing((p) => ({ ...(p || {}), assigned_to_admin_id: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="">Responsable TI (opcional)</option>
              {hvAdminOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <textarea
            value={hvEditing?.notes || ''}
            onChange={(e) => setHvEditing((p) => ({ ...(p || {}), notes: e.target.value }))}
            className="border rounded px-3 py-2 text-sm w-full"
            rows={4}
            placeholder="Notas"
          />
          <div className="flex items-center gap-2 justify-end">
            <button type="button" className="px-3 py-2 rounded border" onClick={() => { setHvEditOpen(false); setHvEditing(null); }}>
              Cancelar
            </button>
            <button type="button" className="px-3 py-2 rounded bg-green-600 text-white disabled:opacity-50" onClick={saveHv} disabled={hvSaving}>
              {hvSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
